/* =====================================================================
   ASHTAVINAYAK PACKAGING · PEOPLE INTELLIGENCE
   Workforce retention analytics portal - redesign of the Retention
   Dashboard. Pair with: UltraTechRetentionDashboard.css, plus
   RetentionWatch.css for the Retention Watch tab.

   Dependencies: react, recharts, lucide-react
   All employee data below is SIMULATED / FICTIONAL.
   The wordmark is a text placeholder, not an official logo.
===================================================================== */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Gauge,
  Users,
  ShieldCheck,
  Building2,
  MessageSquare,
  CalendarRange,
  SlidersHorizontal,
  Bell,
  HelpCircle,
  LogOut,
  Search,
  X,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Minus,
  Lock,
  Download,
  Info,
  AlertTriangle,
  CircleCheck,
  Check,
  Lightbulb,
  RotateCcw,
  MoreHorizontal,
  Eye,
  EyeOff,
  Briefcase,
  MapPin,
  Star,
  ArrowUpDown,
  Radio,
  Activity,
  Upload,
} from "lucide-react";
import "./UltraTechRetentionDashboard.css";
import "./RetentionWatch.css";

/* ---------------------------------------------------------------
   THEME (mirrors CSS variables; needed for chart props)
--------------------------------------------------------------- */
const THEME = {
  yellow: "#f26232",
  ink: "#26272b",
  charcoal: "#303134",
  muted: "#61636e",
  border: "#e3e5ef",
  retained: "#21865A",
  risk: "#C58A00",
  resigned: "#d32f45",
};

/* ---------------------------------------------------------------
   DOMAIN CONSTANTS
--------------------------------------------------------------- */
const QUARTERS = [
  "Q1 '25",
  "Q2 '25",
  "Q3 '25",
  "Q4 '25",
  "Q1 '26",
  "Q2 '26",
  "Q3 '26",
  "Q4 '26",
];
const SURVEY_PARTICIPATION = [88, 90, 86, 91, 93, 95, 92, 94];
const ALL_UNITS = "All business units";
const ALL_DEPTS = "All functions";

const PARAM_KEYS = [
  "careerGrowth",
  "compensation",
  "manager",
  "workLife",
  "recognition",
  "belonging",
  "workload",
];
const PARAM_LABELS = {
  careerGrowth: "Career growth",
  compensation: "Compensation fairness",
  manager: "Manager relationship",
  workLife: "Work-life balance",
  recognition: "Recognition",
  belonging: "Team belonging",
  workload: "Workload sustainability",
};
const PARAM_DESC = {
  careerGrowth: "Advancement & learning clarity",
  compensation: "Market benchmark & pay fairness",
  manager: "Trust, coaching & feedback",
  workLife: "Schedule flexibility & downtime",
  recognition: "Peer appreciation & leadership praise",
  belonging: "Psychological safety & inclusion",
  workload: "Burnout risk & project pacing",
};
const DEFAULT_WEIGHTS = {
  careerGrowth: 20,
  compensation: 15,
  manager: 20,
  workLife: 15,
  recognition: 10,
  belonging: 12,
  workload: 8,
};

const SURVEY_COPY = {
  careerGrowth: {
    eyebrow: "Growth",
    q: "I can see a clear path to grow and develop my career at Ashtavinayak.",
    hint: "Think about learning opportunities, internal moves and how clear your next step feels.",
  },
  compensation: {
    eyebrow: "Fairness",
    q: "I feel my compensation is fair for my role and the market.",
    hint: "Consider your overall package, not only your base pay.",
  },
  manager: {
    eyebrow: "Leadership",
    q: "I trust and feel supported by my direct manager.",
    hint: "Think about coaching, feedback and how safe it feels to raise concerns.",
  },
  workLife: {
    eyebrow: "Balance",
    q: "My workload allows for a healthy balance between work and life.",
    hint: "Reflect on schedules, shifts, travel and time to recharge.",
  },
  recognition: {
    eyebrow: "Recognition",
    q: "My contributions are recognised and appreciated.",
    hint: "From your manager, your peers or senior leaders.",
  },
  belonging: {
    eyebrow: "Belonging",
    q: "I feel a genuine sense of belonging within my team.",
    hint: "Do you feel included, heard and able to be yourself at work?",
  },
  workload: {
    eyebrow: "Sustainability",
    q: "My workload is sustainable, without regular burnout or excessive overtime.",
    hint: "Think about the last few months, not just this week.",
  },
};

/* Business structure (sample) */
const DEPTS = [
  { name: "Manufacturing & Production", headcount: 148 },
  { name: "Sales & Marketing", headcount: 96 },
  { name: "Logistics & Supply Chain", headcount: 71 },
  { name: "Design & Packaging Development", headcount: 44 },
  { name: "Procurement & Raw Materials", headcount: 58 },
  { name: "Technology & Digital", headcount: 39 },
];
const UNITS = [
  "Corrugated Packaging",
  "Flexible & Rigid Packaging",
  "Corporate & Shared Services",
];
const LOCATIONS = [
  "Mumbai HQ",
  "Rajasthan Cluster",
  "Andhra Pradesh Cluster",
  "Gujarat Cluster",
  "Chhattisgarh Cluster",
];
const GENERIC_ROLES = {
  "Manufacturing & Production": [
    "Press Operator",
    "Production Supervisor",
    "Shift Manager",
  ],
  "Sales & Marketing": [
    "Territory Manager",
    "Key Account Executive",
    "Brand Executive",
  ],
  "Logistics & Supply Chain": [
    "Logistics Planner",
    "Dispatch Coordinator",
    "Fleet Analyst",
  ],
  "Design & Packaging Development": [
    "Packaging Design Engineer",
    "Structural Designer",
    "Prepress Specialist",
  ],
  "Procurement & Raw Materials": [
    "Paper & Board Buyer",
    "Commercial Analyst",
    "Vendor Manager",
  ],
  "Technology & Digital": [
    "Software Engineer",
    "Data Analyst",
    "Systems Specialist",
  ],
};
const TALENT_ROLES = {
  "Manufacturing & Production": [
    "Plant Head",
    "Converting Process Lead",
    "Maintenance & Reliability Lead",
    "Head of Quality",
  ],
  "Sales & Marketing": [
    "Regional Sales Head",
    "Key Accounts Lead",
    "Channel Strategy Lead",
  ],
  "Logistics & Supply Chain": [
    "Logistics Network Lead",
    "Supply Planning Lead",
    "Warehouse & Distribution Lead",
  ],
  "Design & Packaging Development": [
    "Principal Designer",
    "Head of Packaging R&D",
    "Prepress Lead",
  ],
  "Procurement & Raw Materials": [
    "Category Head",
    "Strategic Sourcing Lead",
    "Commercial Lead",
  ],
  "Technology & Digital": [
    "Architect",
    "Digital Factory Lead",
    "Data Platform Lead",
  ],
};
const TALENT_REASONS = [
  "Rare technical skill",
  "Key system owner",
  "Revenue contribution",
  "Critical role",
  "Hard to replace",
  "Critical client relationship",
];

const FIRST = [
  "Aditi",
  "Rohan",
  "Meera",
  "Karan",
  "Ishaan",
  "Priya",
  "Vikram",
  "Ananya",
  "Rahul",
  "Sneha",
  "Arjun",
  "Neha",
  "Kabir",
  "Tanvi",
  "Aarav",
  "Diya",
  "Manish",
  "Pooja",
  "Siddharth",
  "Kavya",
  "Rajesh",
  "Nisha",
  "Varun",
  "Shreya",
  "Amit",
  "Divya",
  "Nikhil",
  "Ritu",
  "Sanjay",
  "Isha",
];
const LAST = [
  "Sharma",
  "Verma",
  "Iyer",
  "Nair",
  "Gupta",
  "Khan",
  "Mehta",
  "Reddy",
  "Joshi",
  "Kapoor",
  "Malhotra",
  "Chatterjee",
  "Bose",
  "Rao",
  "Pillai",
  "Menon",
  "Bhatt",
  "Chauhan",
  "Desai",
  "Agarwal",
];

const DRIVER_TIPS = {
  careerGrowth: {
    effort: "Medium",
    text: "Share a visible career path, pair them with a mentor and surface one internal opportunity.",
  },
  compensation: {
    effort: "High",
    text: "Review pay against the market and explain total rewards in plain language.",
  },
  manager: {
    effort: "Medium",
    text: "Support the manager on regular 1:1s. If trust stays low, explore team options together.",
  },
  workLife: {
    effort: "Medium",
    text: "Protect time off and agree which work can wait.",
  },
  recognition: {
    effort: "Low",
    text: "Encourage peer appreciation and specific, timely praise from leaders.",
  },
  belonging: {
    effort: "Low",
    text: "Offer a cross-team role and a regular check-in where they feel heard.",
  },
  workload: {
    effort: "Medium",
    text: "Rebalance tasks, cap recurring overtime and consider flexible hours.",
  },
};
const EFFORT_PLAIN = {
  Low: "Quick to act",
  Medium: "Takes some time",
  High: "Larger initiative",
};

const QUADRANTS = {
  protect: {
    title: "Protect",
    hint: "Higher retention risk · high criticality",
    tone: "critical",
  },
  watch: {
    title: "Watch",
    hint: "Higher retention risk · lower criticality",
    tone: "warning",
  },
  keep: {
    title: "Key Roles",
    hint: "Lower risk · high criticality",
    tone: "neutral",
  },
  stable: {
    title: "Stable",
    hint: "Lower risk · lower criticality",
    tone: "positive",
  },
};

const RISK_META = {
  High: {
    label: "Requires attention",
    color: THEME.resigned,
    hint: "Risk score 55 or above",
  },
  Medium: { label: "Monitor", color: THEME.risk, hint: "Risk score 35 to 54" },
  Low: { label: "Healthy", color: THEME.retained, hint: "Risk score below 35" },
};

/* ---------------------------------------------------------------
   HELPERS
--------------------------------------------------------------- */
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function hashId(id) {
  const s = String(id);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
const round1 = (v) => Math.round(v * 10) / 10;
const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* Adds business unit / location / role to an employee record */
function enrich(e) {
  if (e.unit) return e;
  const r = mulberry32(hashId(e.id) + 7);
  const u = r();
  const unit = u < 0.62 ? UNITS[0] : u < 0.84 ? UNITS[1] : UNITS[2];
  const location = LOCATIONS[Math.floor(r() * LOCATIONS.length)];
  const roles = GENERIC_ROLES[e.department] || ["Specialist"];
  const role = roles[Math.floor(r() * roles.length)];
  return { ...e, unit, location, role };
}

/* ---------------------------------------------------------------
   DETERMINISTIC SIMULATION - one persistent dataset of quarterly
   snapshots feeding every view.
--------------------------------------------------------------- */
function buildSimulation() {
  const rng = mulberry32(20260907);
  const deptPool = [];
  DEPTS.forEach((d) => {
    for (let i = 0; i < d.headcount; i++) deptPool.push(d.name);
  });
  for (let i = deptPool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deptPool[i], deptPool[j]] = [deptPool[j], deptPool[i]];
  }
  let nextId = 1;
  function makeEmployee(department) {
    const id = nextId++;
    return {
      id,
      name: `${FIRST[id % FIRST.length]} ${LAST[Math.floor(id / FIRST.length) % LAST.length]}`,
      department,
      base: Math.pow(rng(), 1.4),
      drift: 0,
    };
  }
  let active = deptPool.map(makeEmployee);
  const snapshots = [];
  for (let t = 0; t < QUARTERS.length; t++) {
    const snapshot = [];
    const newHires = [];
    active.forEach((emp) => {
      emp.drift = emp.drift * 0.55 + (rng() - 0.5) * 2.2;
      const params = {};
      PARAM_KEYS.forEach((k) => {
        const noise = (rng() - 0.5) * 2.4;
        let v = 2.5 + emp.base * 7 + emp.drift * 1.3 + noise;
        v = Math.max(1, Math.min(10, Math.round(v * 10) / 10));
        params[k] = v;
      });
      const avg =
        PARAM_KEYS.reduce((s, k) => s + params[k], 0) / PARAM_KEYS.length;
      let status;
      if (avg < 4.2) status = rng() < 0.35 ? "Resigned" : "At risk";
      else if (avg < 5.8) status = rng() < 0.45 ? "At risk" : "Retained";
      else status = "Retained";
      snapshot.push(
        enrich({
          id: emp.id,
          name: emp.name,
          department: emp.department,
          status,
          params,
          avgScore: Math.round(avg * 10) / 10,
        }),
      );
      if (status === "Resigned") newHires.push(makeEmployee(emp.department));
    });
    snapshots.push(snapshot);
    const resignedIds = new Set(
      snapshot.filter((s) => s.status === "Resigned").map((s) => s.id),
    );
    active = active.filter((e) => !resignedIds.has(e.id)).concat(newHires);
  }
  return snapshots;
}
const SNAPSHOTS = buildSimulation();
const HISTORY = (() => {
  const m = new Map();
  SNAPSHOTS.forEach((snap, qi) =>
    snap.forEach((e) => {
      if (!m.has(e.id)) m.set(e.id, []);
      m.get(e.id).push({
        qi,
        avg: e.avgScore,
        status: e.status,
        params: e.params,
      });
    }),
  );
  return m;
})();

/* ---------------------------------------------------------------
   ANALYTICS
--------------------------------------------------------------- */
function computeRisk(params, weights) {
  const total = PARAM_KEYS.reduce((s, k) => s + weights[k], 0) || 1;
  const raw = PARAM_KEYS.reduce(
    (s, k) => s + (weights[k] / total) * (10 - params[k]),
    0,
  );
  return Math.round(raw * 10);
}
function riskBucket(score) {
  if (score >= 55) return "High";
  if (score >= 35) return "Medium";
  return "Low";
}
function computeEnps(list) {
  if (!list.length) return 0;
  const promoters = list.filter((e) => e.avgScore >= 7.5).length;
  const detractors = list.filter((e) => e.avgScore <= 4.5).length;
  return Math.round(((promoters - detractors) / list.length) * 100);
}
function statusFromAvg(avg) {
  return avg >= 5.8 ? "Retained" : "At risk";
}
function stats(list) {
  const total = list.length;
  const retained = list.filter((e) => e.status === "Retained").length;
  const atRisk = list.filter((e) => e.status === "At risk").length;
  const resigned = list.filter((e) => e.status === "Resigned").length;
  return {
    total,
    retained,
    atRisk,
    resigned,
    rate: total ? (retained / total) * 100 : 0,
    enps: computeEnps(list),
    avg: total ? round1(list.reduce((s, e) => s + e.avgScore, 0) / total) : 0,
  };
}
function driverAvg(list) {
  const o = {};
  PARAM_KEYS.forEach((k) => {
    o[k] = list.length
      ? round1(list.reduce((s, e) => s + e.params[k], 0) / list.length)
      : 0;
  });
  return o;
}
function talentProfile(e) {
  const rng = mulberry32(hashId(e.id));
  if (rng() >= 0.13) return null;
  const roles = TALENT_ROLES[e.department] || ["Specialist"];
  const role = roles[Math.floor(rng() * roles.length)];
  const whyCritical = TALENT_REASONS[Math.floor(rng() * TALENT_REASONS.length)];
  const roll = rng();
  const successor = roll < 0.3 ? "None" : roll < 0.62 ? "Partial" : "Ready";
  const criticality = Math.round(35 + rng() * 60);
  return {
    role,
    whyCritical,
    successor,
    criticality,
    highCriticality: criticality >= 62,
  };
}
function talentQuadrant(bucket, highCriticality) {
  if (bucket === "High" && highCriticality) return "protect";
  if (bucket === "High") return "watch";
  if (highCriticality) return "keep";
  return "stable";
}
function buildTalent(list, weights) {
  const active = [];
  let resigned = 0;
  list.forEach((e) => {
    const p = talentProfile(e);
    if (!p) return;
    if (e.status === "Resigned") {
      resigned += 1;
      return;
    }
    const risk = computeRisk(e.params, weights);
    const bucket = riskBucket(risk);
    active.push({
      ...e,
      ...p,
      risk,
      bucket,
      quadrant: talentQuadrant(bucket, p.highCriticality),
    });
  });
  return { active, resigned };
}
function whyFlagged(params) {
  const weak = PARAM_KEYS.filter((k) => params[k] < 5).sort(
    (a, b) => params[a] - params[b],
  );
  if (!weak.length) {
    const lowest = [...PARAM_KEYS].sort((a, b) => params[a] - params[b])[0];
    return `No response is below 5 out of 10. The lowest score is ${PARAM_LABELS[lowest].toLowerCase()} at ${params[lowest]}/10.`;
  }
  const bits = weak
    .slice(0, 3)
    .map((k) => `${PARAM_LABELS[k].toLowerCase()} (${params[k]}/10)`);
  return `Responses below 5 out of 10 suggest areas worth a supportive conversation: ${bits.join(", ")}.`;
}
function continuityLabel(s) {
  if (s === "None") return "No backup identified";
  if (s === "Partial") return "Backup partially ready";
  return "Backup in place";
}
function deptStatus(rate, atRiskPct) {
  if (rate >= 90 && atRiskPct < 12)
    return { label: "Healthy", tone: "positive" };
  if (rate >= 85) return { label: "Monitor", tone: "warning" };
  return { label: "Needs attention", tone: "critical" };
}

/* CSV export */
function csvCell(v) {
  const s = String(v ?? "");
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}
function exportTalentCsv(rows, quarter) {
  const header = [
    "Name",
    "Function",
    "Business unit",
    "Role",
    "Why critical",
    "Continuity",
    "Retention risk score (0-100)",
    "Risk level",
    "Outcome",
    "Avg score",
    "Survey cycle",
    ...PARAM_KEYS.map((k) => PARAM_LABELS[k]),
  ];
  const lines = [header.map(csvCell).join(",")];
  rows.forEach((e) =>
    lines.push(
      [
        e.name,
        e.department,
        e.unit,
        e.role,
        e.whyCritical,
        continuityLabel(e.successor),
        e.risk,
        RISK_META[e.bucket].label,
        e.status,
        e.avgScore,
        quarter,
        ...PARAM_KEYS.map((k) => e.params[k]),
      ]
        .map(csvCell)
        .join(","),
    ),
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `critical-talent-${quarter.replace(/[^a-z0-9]+/gi, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------------------------------------------------------------
   HOOKS
--------------------------------------------------------------- */
function useMedia(query) {
  const [m, setM] = useState(
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const h = () => setM(mq.matches);
    h();
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [query]);
  return m;
}
function useBriefLoading(deps, ms = 420) {
  const [l, setL] = useState(true);
  useEffect(() => {
    setL(true);
    const t = setTimeout(() => setL(false), ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return l;
}
function useCountUp(target, decimals = 0, duration = 700) {
  const [v, setV] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const s = from.current;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = s + (target - s) * eased;
      from.current = val;
      setV(val);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return Number(v).toFixed(decimals);
}

/* ---------------------------------------------------------------
   DATA CONTEXT
--------------------------------------------------------------- */
const DataCtx = React.createContext(null);
const useData = () => React.useContext(DataCtx);

/* ---------------------------------------------------------------
   PRIMITIVES
--------------------------------------------------------------- */
function Wordmark({ light, compact }) {
  return (
    <div className={`ap-wordmark ${light ? "is-light" : ""}`}>
      <img
        src="/ashtavinayak-packaging-logo.png"
        alt="Ashtavinayak Packaging Pvt Ltd"
        className="ap-wordmark-logo"
      />

      <div>
        <div className="ap-wordmark-name">Ashtavinayak</div>
        {!compact && <div className="ap-wordmark-sub">People Intelligence</div>}
      </div>
    </div>
  );
}
function Skeleton({ h = 16, w = "100%", r = 6, style }) {
  return (
    <span
      className="ap-skel"
      style={{ height: h, width: w, borderRadius: r, ...style }}
    />
  );
}
function Tip({ text }) {
  return (
    <span className="ap-tip" data-tip={text} tabIndex={0} aria-label={text}>
      <Info size={13} />
    </span>
  );
}
function Avatar({ name, size = 34, tone }) {
  return (
    <span
      className={`ap-avatar ${tone ? `tone-${tone}` : ""}`}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials(name)}
    </span>
  );
}
function Sparkline({ data, color = THEME.ink, w = 96, h = 30 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - 3 - ((v - min) / span) * (h - 6),
  ]);
  const d = pts
    .map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(" ");
  const last = pts[pts.length - 1];
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      className="ap-spark"
    >
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}
function Delta({
  value,
  unit = "",
  goodWhen = "up",
  digits = 1,
  noPriorLabel = "No prior cycle",
}) {
  if (value == null)
    return <span className="ap-delta is-flat">{noPriorLabel}</span>;
  const flat = Math.abs(value) < 0.05;
  const up = value > 0;
  const good = flat ? null : (goodWhen === "up") === up;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`ap-delta ${flat ? "is-flat" : good ? "is-good" : "is-bad"}`}
    >
      <Icon size={13} />
      {up && !flat ? "+" : ""}
      {value.toFixed(digits)}
      {unit}
    </span>
  );
}
function StatusPill({ status }) {
  const cls =
    status === "Retained"
      ? "positive"
      : status === "At risk"
        ? "warning"
        : "critical";
  return <span className={`ap-pill tone-${cls}`}>{status}</span>;
}
function RiskBand({ bucket, score, compact }) {
  const m = RISK_META[bucket] || RISK_META.Low;
  const tone =
    bucket === "High"
      ? "critical"
      : bucket === "Medium"
        ? "warning"
        : "positive";
  const Icon =
    bucket === "High"
      ? AlertTriangle
      : bucket === "Medium"
        ? Minus
        : CircleCheck;
  return (
    <span className={`ap-risk tone-${tone}`}>
      <Icon size={13} aria-hidden="true" />
      {!compact && <span>{m.label}</span>}
      <b>{score}</b>
    </span>
  );
}
function Continuity({ level, showLabel = true }) {
  const filled = level === "Ready" ? 3 : level === "Partial" ? 2 : 0;
  const tone =
    level === "Ready"
      ? "positive"
      : level === "Partial"
        ? "warning"
        : "critical";
  return (
    <span className={`ap-continuity tone-${tone}`}>
      <span className="ap-continuity-bars" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <i key={i} className={i < filled ? "on" : ""} />
        ))}
      </span>
      {showLabel && <span>{continuityLabel(level)}</span>}
    </span>
  );
}
function EmptyState({ icon: Icon = Search, title, text, action, onAction }) {
  return (
    <div className="ap-empty">
      <span className="ap-empty-icon">
        <Icon size={20} />
      </span>
      <div className="ap-empty-title">{title}</div>
      <p>{text}</p>
      {action && (
        <button
          type="button"
          className="ap-btn ap-btn-ghost"
          onClick={onAction}
        >
          {action}
        </button>
      )}
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="ap-select">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
function Pager({ page, pages, onPage, from, to, total }) {
  if (!total) return null;
  return (
    <div className="ap-pager">
      <span>
        Showing {from}–{to} of {total}
      </span>
      {pages > 1 && (
        <div className="ap-pager-ctl">
          <button
            type="button"
            className="ap-btn ap-btn-ghost ap-btn-sm"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="ap-btn ap-btn-ghost ap-btn-sm"
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
function Th({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
  align = "left",
  className = "",
}) {
  const active = sortKey === k;
  return (
    <th
      className={`${className} ${active ? "is-sorted" : ""}`}
      style={{ textAlign: align }}
    >
      {onSort ? (
        <button type="button" onClick={() => onSort(k)} className="ap-th-btn">
          {label}
          {active ? (
            <span aria-hidden="true">{sortDir === "asc" ? "↑" : "↓"}</span>
          ) : (
            <ArrowUpDown size={11} />
          )}
        </button>
      ) : (
        label
      )}
    </th>
  );
}
function PrivacyNote({ children }) {
  return (
    <div className="ap-privacy">
      <Lock size={13} />
      <span>
        {children ||
          "Confidential People Analytics · Access restricted to authorized HR users."}
      </span>
    </div>
  );
}

function Drawer({ open, onClose, children, label }) {
  const [mounted, setMounted] = useState(open);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVis(true)),
      );
      return () => cancelAnimationFrame(r);
    }
    setVis(false);
    const t = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(t);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!mounted) return null;
  return (
    <div className={`ap-drawer-root ${vis ? "is-open" : ""}`}>
      <div className="ap-drawer-scrim" onClick={onClose} />
      <aside
        className="ap-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </aside>
    </div>
  );
}

function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <header className="ap-page-head">
      <div className="ap-page-head-main">
        <div className="ap-eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children && <div className="ap-page-head-side">{children}</div>}
    </header>
  );
}

function FilterBar({ show = ["quarter", "unit", "dept"] }) {
  const d = useData();
  const [open, setOpen] = useState(false);
  const active =
    (d.unit !== ALL_UNITS ? 1 : 0) + (d.dept !== ALL_DEPTS ? 1 : 0);
  return (
    <div className="ap-filters">
      <button
        type="button"
        className="ap-filters-toggle ap-btn ap-btn-ghost"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <SlidersHorizontal size={14} /> Filters
        {active > 0 && <span className="ap-count-badge">{active}</span>}
      </button>
      <div className={`ap-filters-body ${open ? "is-open" : ""}`}>
        {show.includes("quarter") && (
          <Select
            label="Survey cycle"
            value={d.quarter}
            onChange={d.setQuarter}
            options={QUARTERS}
          />
        )}
        {show.includes("unit") && (
          <Select
            label="Business unit"
            value={d.unit}
            onChange={d.setUnit}
            options={[ALL_UNITS, ...UNITS]}
          />
        )}
        {show.includes("dept") && (
          <Select
            label="Function"
            value={d.dept}
            onChange={d.setDept}
            options={[ALL_DEPTS, ...DEPTS.map((x) => x.name)]}
          />
        )}
        {active > 0 && (
          <button
            type="button"
            className="ap-link"
            onClick={() => {
              d.setUnit(ALL_UNITS);
              d.setDept(ALL_DEPTS);
            }}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   CHARTS
--------------------------------------------------------------- */
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const total = row.retained + row.atRisk + row.resigned || 1;
  const items = [
    ["Retained", row.retained, THEME.retained],
    ["At risk", row.atRisk, THEME.risk],
    ["Resigned", row.resigned, THEME.resigned],
  ];
  return (
    <div className="ap-chart-tip">
      <div className="ap-chart-tip-h">{label}</div>
      {items.map(([n, v, c]) => (
        <div key={n} className="ap-chart-tip-r">
          <span>
            <i style={{ background: c }} />
            {n}
          </span>
          <b>
            {v} <em>({((v / total) * 100).toFixed(1)}%)</em>
          </b>
        </div>
      ))}
      <div className="ap-chart-tip-f">
        Retention rate <b>{((row.retained / total) * 100).toFixed(1)}%</b>
      </div>
    </div>
  );
}

function TrendChart({ data, quarter, onQuarter, mode = "count", height }) {
  const isMobile = useMedia("(max-width: 720px)");
  return (
    <ResponsiveContainer width="100%" height={height || (isMobile ? 230 : 310)}>
      <AreaChart
        data={data}
        stackOffset={mode === "share" ? "expand" : "none"}
        margin={{ top: 10, right: 8, left: isMobile ? -18 : -6, bottom: 0 }}
        onClick={(s) =>
          s && s.activeLabel && onQuarter && onQuarter(s.activeLabel)
        }
      >
        <defs>
          {[
            ["rt", THEME.retained],
            ["rk", THEME.risk],
            ["rs", THEME.resigned],
          ].map(([id, c]) => (
            <linearGradient
              key={id}
              id={`ap-${id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={c} stopOpacity={0.5} />
              <stop offset="100%" stopColor={c} stopOpacity={0.08} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="#eceef6" vertical={false} />
        <XAxis
          dataKey="q"
          tickLine={false}
          axisLine={{ stroke: "#dcdfea" }}
          tick={{ fontSize: 12, fill: THEME.muted }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={isMobile ? 36 : 44}
          tick={{ fontSize: 12, fill: THEME.muted }}
          tickFormatter={
            mode === "share" ? (v) => `${Math.round(v * 100)}%` : undefined
          }
        />
        <Tooltip
          content={<TrendTooltip />}
          cursor={{ stroke: THEME.ink, strokeDasharray: "3 3" }}
        />
        <ReferenceLine x={quarter} stroke={THEME.yellow} strokeWidth={2.5} />
        <Area
          type="monotone"
          dataKey="retained"
          name="Retained"
          stackId="s"
          stroke={THEME.retained}
          strokeWidth={2}
          fill="url(#ap-rt)"
        />
        <Area
          type="monotone"
          dataKey="atRisk"
          name="At risk"
          stackId="s"
          stroke={THEME.risk}
          strokeWidth={2}
          fill="url(#ap-rk)"
        />
        <Area
          type="monotone"
          dataKey="resigned"
          name="Resigned"
          stackId="s"
          stroke={THEME.resigned}
          strokeWidth={2}
          fill="url(#ap-rs)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function RiskDonut({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  const high = segments.find((s) => s.key === "High")?.value || 0;
  return (
    <div className="ap-donut">
      <div className="ap-donut-chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              innerRadius="68%"
              outerRadius="96%"
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {segments.map((s) => (
                <Cell key={s.key} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="ap-chart-tip">
                    <div className="ap-chart-tip-h">{payload[0].name}</div>
                    <div className="ap-chart-tip-r">
                      <span>Employees</span>
                      <b>{payload[0].value}</b>
                    </div>
                  </div>
                ) : null
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="ap-donut-center">
          <div className="ap-donut-num">{high}</div>
          <div className="ap-donut-lab">Require attention</div>
        </div>
      </div>
      <ul className="ap-legend-list">
        {segments.map((s) => (
          <li key={s.key}>
            <i style={{ background: s.color }} />
            <div>
              <div className="ap-legend-name">{s.label}</div>
              <div className="ap-legend-hint">{s.hint}</div>
            </div>
            <div className="ap-legend-val">
              <b>{s.value}</b>
              <span>{total ? ((s.value / total) * 100).toFixed(0) : 0}%</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SC_MIN = 1;
const SC_MAX = 10;
const scPct = (v) => ((v - SC_MIN) / (SC_MAX - SC_MIN)) * 100;

function DriverDot({ value, color, name }) {
  if (!value) return null;
  return (
    <div
      className="ap-dd-dot"
      style={{ left: `${scPct(value)}%` }}
      title={`${name}: ${value.toFixed(1)}`}
    >
      <span className="ap-dd-val" style={{ color }}>
        {value.toFixed(1)}
      </span>
      <span className="ap-dd-ring" style={{ borderColor: color }}>
        <i style={{ background: color }} />
      </span>
    </div>
  );
}
function DriverComparison({ data }) {
  const rows = data
    .map((d) => {
      const ok = d.Retained > 0 && d.Resigned > 0;
      return { ...d, ok, div: ok ? d.Retained - d.Resigned : null };
    })
    .sort((a, b) => (b.div ?? -99) - (a.div ?? -99));
  return (
    <div className="ap-dd">
      <div className="ap-dd-legend">
        <span className="ap-dd-scale">Scale 1.0 (low) → 10.0 (high)</span>
        {[
          ["Retained", THEME.retained],
          ["At risk", THEME.risk],
          ["Resigned", THEME.resigned],
        ].map(([n, c]) => (
          <span key={n}>
            <i style={{ background: c }} />
            {n}
          </span>
        ))}
      </div>
      <div className="ap-dd-head">
        <span>Retention driver</span>
        <div className="ap-dd-axis">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <span key={n} style={{ left: `${scPct(n)}%` }}>
              {n}
            </span>
          ))}
        </div>
        <span className="ap-center">Perception gap</span>
      </div>
      {rows.map((r) => {
        const lo = Math.min(r.Resigned || r.Retained, r.Retained);
        const hi = Math.max(r.Resigned, r.Retained);
        return (
          <div className="ap-dd-row" key={r.key}>
            <div className="ap-dd-label">
              <div>{r.parameter}</div>
              <small>{PARAM_DESC[r.key]}</small>
            </div>
            <div className="ap-dd-track">
              <div className="ap-dd-line" />
              {r.ok && (
                <div
                  className="ap-dd-bridge"
                  style={{
                    left: `${scPct(lo)}%`,
                    width: `${scPct(hi) - scPct(lo)}%`,
                    background: `linear-gradient(to right, ${THEME.resigned}, ${THEME.risk}, ${THEME.retained})`,
                  }}
                />
              )}
              <DriverDot
                value={r.Resigned}
                color={THEME.resigned}
                name="Resigned"
              />
              <DriverDot
                value={r["At risk"]}
                color={THEME.risk}
                name="At risk"
              />
              <DriverDot
                value={r.Retained}
                color={THEME.retained}
                name="Retained"
              />
            </div>
            <div className="ap-dd-gap">
              <span className="ap-gap-chip">
                {r.div === null
                  ? "n/a"
                  : `${r.div >= 0 ? "+" : ""}${r.div.toFixed(1)} pts`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------
   KPI
--------------------------------------------------------------- */
function Kpi({
  label,
  tip,
  value,
  decimals = 0,
  unit,
  delta,
  deltaProps,
  caption,
  spark,
  sparkColor,
  accent,
}) {
  const shown = useCountUp(value, decimals);
  return (
    <article className={`ap-kpi ${accent ? "is-accent" : ""}`}>
      <div className="ap-kpi-top">
        <span className="ap-kpi-label">{label}</span>
        {tip && <Tip text={tip} />}
      </div>
      <div className="ap-kpi-main">
        <div className="ap-kpi-value">
          {shown}
          {unit && <span className="ap-kpi-unit">{unit}</span>}
        </div>
        {spark && <Sparkline data={spark} color={sparkColor || THEME.ink} />}
      </div>
      <div className="ap-kpi-foot">
        <Delta value={delta} {...deltaProps} />
        <span className="ap-kpi-caption">{caption}</span>
      </div>
    </article>
  );
}
function KpiSkeleton() {
  return (
    <article className="ap-kpi">
      <Skeleton h={12} w="50%" />
      <Skeleton h={38} w="60%" style={{ margin: "16px 0" }} />
      <Skeleton h={12} w="80%" />
    </article>
  );
}

/* ---------------------------------------------------------------
   DEPARTMENT TABLE (dashboard)
--------------------------------------------------------------- */
function barScale(rate) {
  return Math.max(0, Math.min(100, ((rate - 70) / 30) * 100));
}
function DepartmentIntelligence() {
  const d = useData();
  const rows = d.deptRows.filter((r) => r.s.total > 0);
  if (!rows.length)
    return (
      <EmptyState
        icon={Building2}
        title="No headcount in this business unit"
        text="Choose another business unit to compare functions."
      />
    );
  return (
    <div className="ap-table-wrap">
      <table className="ap-table ap-table-dept">
        <thead>
          <tr>
            <th>Function</th>
            <th className="ap-num">Headcount</th>
            <th>Retention rate</th>
            <th className="hide-md">At risk</th>
            <th className="ap-num hide-sm">Resigned</th>
            <th>Trend vs prior cycle</th>
            <th className="hide-md">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const st = deptStatus(r.s.rate, r.atRiskPct);
            const color =
              r.s.rate >= 90
                ? THEME.retained
                : r.s.rate >= 85
                  ? THEME.risk
                  : THEME.resigned;
            return (
              <tr
                key={r.name}
                className={d.dept === r.name ? "is-selected" : ""}
                onClick={() => {
                  d.setDeptDetail(r.name);
                  d.go("departments");
                }}
              >
                <td>
                  <div className="ap-cell-title">{r.name}</div>
                </td>
                <td className="ap-num">{r.s.total}</td>
                <td>
                  <div className="ap-bar-cell">
                    <div className="ap-bar">
                      <i
                        style={{
                          width: `${barScale(r.s.rate)}%`,
                          background: color,
                        }}
                      />
                    </div>
                    <b>{r.s.rate.toFixed(1)}%</b>
                  </div>
                </td>
                <td className="hide-md">
                  <div className="ap-bar-cell">
                    <div className="ap-bar">
                      <i
                        style={{
                          width: `${Math.min(100, r.atRiskPct * 4)}%`,
                          background: THEME.risk,
                        }}
                      />
                    </div>
                    <b>{r.s.atRisk}</b>
                    <small>{r.atRiskPct.toFixed(1)}%</small>
                  </div>
                </td>
                <td className="ap-num hide-sm">{r.s.resigned}</td>
                <td>
                  <div className="ap-trend-cell">
                    <Delta value={r.delta} unit=" pts" />
                    <Sparkline
                      data={r.rateSeries}
                      color={color}
                      w={64}
                      h={22}
                    />
                  </div>
                </td>
                <td className="hide-md">
                  <span className={`ap-pill tone-${st.tone}`}>{st.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------
   EMPLOYEE / TALENT DRAWER
--------------------------------------------------------------- */
function EmployeeDrawer({ emp, onClose }) {
  const { qIndex } = useData();
  const tp =
    emp.criticality != null ? emp : { ...emp, ...(talentProfile(emp) || {}) };
  const isTalent = tp.criticality != null;
  const quad = isTalent ? talentQuadrant(emp.bucket, tp.highCriticality) : null;
  const hist = (
    HISTORY.get(emp.id) || [
      { qi: qIndex, avg: emp.avgScore, status: emp.status, params: emp.params },
    ]
  ).filter((h) => h.qi <= qIndex);
  const sorted = PARAM_KEYS.slice().sort(
    (a, b) => emp.params[a] - emp.params[b],
  );
  const weak = sorted.filter((k) => emp.params[k] < 6).slice(0, 3);
  const tone = (v) =>
    v >= 6.5 ? THEME.retained : v >= 4.5 ? THEME.risk : THEME.resigned;
  return (
    <>
      <div className="ap-drawer-head">
        <div className="ap-drawer-id">
          <Avatar
            name={emp.name}
            size={52}
            tone={quad ? QUADRANTS[quad].tone : undefined}
          />
          <div>
            <h2>{emp.name}</h2>
            <p>
              {tp.role} · {emp.department}
            </p>
            <p className="ap-drawer-meta">
              <MapPin size={12} />
              {emp.location} · {emp.unit}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="ap-icon-btn"
          onClick={onClose}
          aria-label="Close profile"
        >
          <X size={18} />
        </button>
      </div>
      <div className="ap-drawer-body">
        <div className="ap-chip-row">
          <StatusPill status={emp.status} />
          <RiskBand bucket={emp.bucket} score={emp.risk} />
          {quad && (
            <span className={`ap-pill tone-${QUADRANTS[quad].tone}`}>
              {QUADRANTS[quad].title}
            </span>
          )}
        </div>

        {isTalent && (
          <section className="ap-drawer-sec">
            <h3>Talent profile</h3>
            <div className="ap-talent-facts">
              <div>
                <small>Why critical</small>
                <b>{tp.whyCritical}</b>
              </div>
              <div>
                <small>Criticality</small>
                <b>{tp.criticality}/100</b>
              </div>
              <div>
                <small>Continuity</small>
                <Continuity level={tp.successor} />
              </div>
            </div>
          </section>
        )}

        <section className="ap-drawer-sec">
          <h3>Sentiment</h3>
          <div className="ap-sent">
            <div className="ap-sent-num">
              {emp.avgScore}
              <span>/10</span>
            </div>
            <Sparkline
              data={hist.map((h) => h.avg)}
              color={tone(emp.avgScore)}
              w={140}
              h={40}
            />
          </div>
        </section>

        <section className="ap-drawer-sec">
          <h3>Retention drivers</h3>
          {sorted.map((k) => {
            const v = emp.params[k];
            return (
              <div key={k} className="ap-meter">
                <div className="ap-meter-h">
                  <span>{PARAM_LABELS[k]}</span>
                  <b>{v}/10</b>
                </div>
                <div className="ap-meter-track">
                  <i style={{ width: `${v * 10}%`, background: tone(v) }} />
                </div>
              </div>
            );
          })}
        </section>

        <section className="ap-drawer-sec">
          <h3>Insight</h3>
          <p className="ap-body">
            {whyFlagged(emp.params)} Scores are a guide to where a conversation
            may help, not a prediction about any individual.
          </p>
        </section>

        <section className="ap-drawer-sec">
          <h3>
            <Lightbulb size={14} /> Suggested conversation areas
          </h3>
          {weak.length ? (
            weak.map((k) => (
              <div className="ap-suggest" key={k}>
                <div className="ap-suggest-h">
                  <b>
                    {PARAM_LABELS[k]} · {emp.params[k]}/10
                  </b>
                  <span className="ap-pill tone-neutral">
                    {EFFORT_PLAIN[DRIVER_TIPS[k].effort]}
                  </span>
                </div>
                <p>{DRIVER_TIPS[k].text}</p>
              </div>
            ))
          ) : (
            <p className="ap-body">
              All drivers are at 6 or above. A regular check-in is enough.
            </p>
          )}
        </section>

        <section className="ap-drawer-sec">
          <h3>Survey history</h3>
          <ul className="ap-hist">
            {hist
              .slice()
              .reverse()
              .map((h) => (
                <li key={h.qi}>
                  <span>{QUARTERS[h.qi]}</span>
                  <b>{h.avg}</b>
                  <StatusPill status={h.status} />
                </li>
              ))}
          </ul>
        </section>
        <PrivacyNote>
          Employee-level information is restricted to authorized HR users.
          Sample data shown is fictional.
        </PrivacyNote>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------
   VIEW: LOGIN
--------------------------------------------------------------- */
function Login({ onSignIn }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!email.trim() || !pw) {
      setErr("Enter your corporate email and password to continue.");
      return;
    }
    setErr("");
    setBusy(true);
    setTimeout(onSignIn, 800);
  };
  return (
    <div className="ap-login">
      <aside className="ap-login-art">
        <Wordmark light />
        <div className="ap-login-bars" aria-hidden="true">
          {[22, 34, 46, 58, 70, 82, 94].map((h, i) => (
            <span
              key={i}
              style={{ height: `${h}%`, animationDelay: `${i * 70}ms` }}
              className={i === 6 ? "is-top" : ""}
            />
          ))}
        </div>
        <div className="ap-login-copy">
          <p className="ap-login-kicker">
            Employee listening · Retention strategy · Continuity
          </p>
          <h2>Turning employee listening into better workforce decisions.</h2>
        </div>
      </aside>
      <main className="ap-login-panel">
        <form className="ap-login-card" onSubmit={submit} noValidate>
          <div className="ap-login-mobile-brand">
            <Wordmark />
          </div>
          <h1>Welcome to People Intelligence</h1>
          <p>Sign in with your Ashtavinayak corporate account.</p>
          <label className="ap-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@ashtavinayak.com"
            />
          </label>
          <label className="ap-field">
            <span>Password</span>
            <div className="ap-pw">
              <input
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>
          {err && (
            <div className="ap-form-err" role="alert">
              {err}
            </div>
          )}
          <button
            type="submit"
            className="ap-btn ap-btn-primary ap-btn-block"
            disabled={busy}
          >
            {busy ? "Signing in…" : "Sign in"}{" "}
            {!busy && <ArrowRight size={16} />}
          </button>
          <PrivacyNote />
        </form>
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: DASHBOARD
--------------------------------------------------------------- */
function DashboardView() {
  const d = useData();
  const loading = useBriefLoading([d.quarter, d.unit, d.dept]);
  const [mode, setMode] = useState("count");
  const { cur, prev, series } = d;
  const today = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const widest = useMemo(
    () =>
      d.driverRows
        .slice()
        .filter((r) => r.Retained && r.Resigned)
        .sort((a, b) => b.Retained - b.Resigned - (a.Retained - a.Resigned))[0],
    [d.driverRows],
  );
  const atRiskPct = cur.total ? (cur.atRisk / cur.total) * 100 : 0;
  const atRiskPctPrev =
    prev && prev.total ? (prev.atRisk / prev.total) * 100 : null;

  return (
    <div className="ap-view">
      <section className="ap-hero">
        <div className="ap-hero-copy">
          <div className="ap-eyebrow is-light">Retention Analysis</div>
          <h1>People Retention Intelligence</h1>
          <p>
            Understand workforce sentiment, retention risk and critical talent
            across Ashtavinayak.
          </p>
          <div className="ap-hero-meta">
            <span>
              <Radio size={12} /> Survey cycle {d.quarter}
            </span>
            <span>{cur.total} employees in view</span>
            <span>Last updated {today}</span>
          </div>
        </div>
        <FilterBar />
      </section>

      <section className="ap-kpi-grid" aria-label="Key workforce indicators">
        {loading ? (
          [0, 1, 2, 3].map((i) => <KpiSkeleton key={i} />)
        ) : (
          <>
            <Kpi
              accent
              label="Retention rate"
              tip="Share of surveyed employees whose outcome status is Retained in this cycle."
              value={cur.rate}
              decimals={1}
              unit="%"
              delta={prev ? cur.rate - prev.rate : null}
              deltaProps={{ unit: " pts" }}
              caption="vs previous cycle"
              spark={series.map((s) => s.rate)}
              sparkColor={THEME.retained}
            />
            <Kpi
              label="Employees at risk"
              tip="Employees flagged At risk by survey outcome this cycle."
              value={cur.atRisk}
              unit=" people"
              delta={prev ? cur.atRisk - prev.atRisk : null}
              deltaProps={{ goodWhen: "down", digits: 0 }}
              caption={`${atRiskPct.toFixed(1)}% of workforce${atRiskPctPrev != null ? ` · was ${atRiskPctPrev.toFixed(1)}%` : ""}`}
              spark={series.map((s) => s.atRisk)}
              sparkColor={THEME.risk}
            />
            <Kpi
              label="Resignations"
              tip="Employees who resigned during this survey cycle."
              value={cur.resigned}
              unit=" people"
              delta={prev ? cur.resigned - prev.resigned : null}
              deltaProps={{ goodWhen: "down", digits: 0 }}
              caption={
                prev
                  ? `${prev.resigned} in previous cycle`
                  : `out of ${cur.total} surveyed`
              }
              spark={series.map((s) => s.resigned)}
              sparkColor={THEME.resigned}
            />
            <Kpi
              label="Employee NPS"
              tip="Promoters (average 7.5+) minus detractors (4.5 or below), as a percentage of respondents."
              value={cur.enps}
              unit=" score"
              delta={prev ? cur.enps - prev.enps : null}
              deltaProps={{ digits: 0 }}
              caption="vs previous cycle"
              spark={series.map((s) => s.enps)}
              sparkColor={THEME.ink}
            />
          </>
        )}
      </section>

      <section className="ap-attn">
        <div className="ap-attn-head">
          <h2>
            <AlertTriangle size={16} /> {d.insights.length} area
            {d.insights.length === 1 ? "" : "s"} need attention
          </h2>
          <span>Organizational insights generated from this cycle's data</span>
        </div>
        <div className="ap-attn-grid">
          {loading
            ? [0, 1, 2].map((i) => (
                <div className="ap-attn-card" key={i}>
                  <Skeleton h={14} w="70%" />
                  <Skeleton h={12} style={{ marginTop: 12 }} />
                  <Skeleton h={12} w="60%" style={{ marginTop: 6 }} />
                </div>
              ))
            : d.insights.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  className={`ap-attn-card tone-${it.tone}`}
                  onClick={it.onClick}
                >
                  <span className="ap-attn-tag">{it.tag}</span>
                  <b>{it.title}</b>
                  <p>{it.detail}</p>
                  <span className="ap-attn-cta">
                    {it.cta} <ChevronRight size={14} />
                  </span>
                </button>
              ))}
        </div>
      </section>

      <div className="ap-grid-8-4">
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Retention trend</h2>
              <p>
                Retained, at-risk and resigned employees by survey cycle ·
                select a cycle to focus
              </p>
            </div>
            <div className="ap-seg" role="tablist" aria-label="Chart mode">
              {[
                ["count", "Headcount"],
                ["share", "Share"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={mode === k}
                  className={mode === k ? "is-on" : ""}
                  onClick={() => setMode(k)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <Skeleton h={280} r={10} />
          ) : (
            <TrendChart
              data={series}
              quarter={d.quarter}
              onQuarter={d.setQuarter}
              mode={mode}
            />
          )}
          <div className="ap-quarter-pills">
            {QUARTERS.map((q) => (
              <button
                key={q}
                type="button"
                className={q === d.quarter ? "is-on" : ""}
                onClick={() => d.setQuarter(q)}
              >
                {q}
              </button>
            ))}
          </div>
          <div className="ap-legend-inline">
            {[
              ["Retained", THEME.retained],
              ["At risk", THEME.risk],
              ["Resigned", THEME.resigned],
            ].map(([n, c]) => (
              <span key={n}>
                <i style={{ background: c }} />
                {n}
              </span>
            ))}
          </div>
        </section>

        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Workforce risk</h2>
              <p>
                {d.quarter} · weighted risk score across {cur.total} employees
              </p>
            </div>
            <Tip text="Employees are grouped by a weighted score of their survey answers. Weights are set in Model Settings." />
          </div>
          {loading ? (
            <Skeleton h={240} r={10} />
          ) : (
            <RiskDonut segments={d.riskSegments} />
          )}
          <p className="ap-card-foot">
            Scored using the weights in{" "}
            <button
              type="button"
              className="ap-link"
              onClick={() => d.go("settings")}
            >
              Model Settings
            </button>
            . Higher scores signal where support may help - not a forecast about
            any person.
          </p>
        </section>
      </div>

      <section className="ap-card">
        <div className="ap-card-head">
          <div>
            <h2>Department intelligence</h2>
            <p>
              Functions compared on headcount, retention and workforce risk ·{" "}
              {d.quarter}
            </p>
          </div>
        </div>
        {loading ? <Skeleton h={220} r={10} /> : <DepartmentIntelligence />}
      </section>

      <section className="ap-card">
        <div className="ap-card-head">
          <div>
            <h2>Retention drivers</h2>
            <p>
              Average survey score by outcome group · where retained and
              resigned employees diverge most
            </p>
          </div>
        </div>
        {loading ? (
          <Skeleton h={300} r={10} />
        ) : (
          <DriverComparison data={d.driverRows} />
        )}
        {widest && !loading && (
          <div className="ap-callout">
            <Lightbulb size={16} />
            <p>
              <b>{widest.parameter}</b> shows the largest perception gap between
              retained ({widest.Retained}) and resigned ({widest.Resigned})
              employees
              {d.dept !== ALL_DEPTS ? ` in ${d.dept}` : ""} this cycle - a
              strong starting point for retention action.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: EMPLOYEES
--------------------------------------------------------------- */
const PAGE = 12;
function EmployeesView() {
  const d = useData();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All outcomes");
  const [risk, setRisk] = useState("All risk levels");
  const [sortKey, setSortKey] = useState("risk");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const loading = useBriefLoading(
    [d.quarter, d.unit, d.dept, q, status, risk],
    300,
  );

  const rows = useMemo(() => {
    let r = d.withRisk;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((e) =>
        `${e.name} ${e.department} ${e.role}`.toLowerCase().includes(s),
      );
    }
    if (status !== "All outcomes") r = r.filter((e) => e.status === status);
    if (risk !== "All risk levels")
      r = r.filter((e) => RISK_META[e.bucket].label === risk);
    return r.slice().sort((a, b) => {
      const av =
        sortKey === "name" ? a.name : sortKey === "avg" ? a.avgScore : a.risk;
      const bv =
        sortKey === "name" ? b.name : sortKey === "avg" ? b.avgScore : b.risk;
      if (typeof av === "string")
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [d.withRisk, q, status, risk, sortKey, sortDir]);

  useEffect(
    () => setPage(1),
    [q, status, risk, d.quarter, d.unit, d.dept, sortKey, sortDir],
  );
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const start = (Math.min(page, pages) - 1) * PAGE;
  const slice = rows.slice(start, start + PAGE);
  const onSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir(k === "name" ? "asc" : "desc");
    }
  };
  const clear = () => {
    setQ("");
    setStatus("All outcomes");
    setRisk("All risk levels");
    d.setUnit(ALL_UNITS);
    d.setDept(ALL_DEPTS);
  };

  return (
    <div className="ap-view">
      <PageHeader
        eyebrow="Employee experience"
        title="Employees"
        subtitle="Search colleagues, review sentiment and open a confidential profile."
      >
        <FilterBar />
      </PageHeader>
      <section className="ap-card ap-card-flush">
        <div className="ap-toolbar">
          <label className="ap-search">
            <Search size={15} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, function or role"
              aria-label="Search employees"
            />
          </label>
          <Select
            label="Outcome"
            value={status}
            onChange={setStatus}
            options={["All outcomes", "Retained", "At risk", "Resigned"]}
          />
          <Select
            label="Workforce risk"
            value={risk}
            onChange={setRisk}
            options={[
              "All risk levels",
              "Healthy",
              "Monitor",
              "Requires attention",
            ]}
          />
        </div>
        {loading ? (
          <div className="ap-skel-rows">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} h={42} r={8} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No employees match these filters"
            text="Try a different search or clear your filters to see the full workforce."
            action="Clear filters"
            onAction={clear}
          />
        ) : (
          <>
            <div className="ap-table-wrap">
              <table className="ap-table ap-table-emp">
                <thead>
                  <tr>
                    <Th
                      label="Employee"
                      k="name"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                      className="ap-sticky"
                    />
                    <Th label="Function" k="dept" className="hide-md" />
                    <Th label="Outcome" k="status" />
                    <Th
                      label="Risk"
                      k="risk"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <Th
                      label="Sentiment"
                      k="avg"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <Th label="Key concern" k="concern" className="hide-md" />
                    <Th label="Participation" k="part" className="hide-sm" />
                    <Th label="Last cycle" k="last" className="hide-sm" />
                  </tr>
                </thead>
                <tbody>
                  {slice.map((e) => {
                    const lowest = PARAM_KEYS.slice().sort(
                      (a, b) => e.params[a] - e.params[b],
                    )[0];
                    const h = HISTORY.get(e.id);
                    const responded = h
                      ? h.filter((x) => x.qi <= d.qIndex).length
                      : 1;
                    const col =
                      e.avgScore >= 6.5
                        ? THEME.retained
                        : e.avgScore >= 4.5
                          ? THEME.risk
                          : THEME.resigned;
                    return (
                      <tr
                        key={e.id}
                        onClick={() => d.openPerson(e)}
                        tabIndex={0}
                        onKeyDown={(ev) =>
                          ev.key === "Enter" && d.openPerson(e)
                        }
                      >
                        <td className="ap-sticky">
                          <div className="ap-person">
                            <Avatar name={e.name} />
                            <div>
                              <div className="ap-cell-title">{e.name}</div>
                              <small>{e.role}</small>
                            </div>
                          </div>
                        </td>
                        <td className="hide-md">{e.department}</td>
                        <td>
                          <StatusPill status={e.status} />
                        </td>
                        <td>
                          <RiskBand bucket={e.bucket} score={e.risk} compact />
                        </td>
                        <td>
                          <div className="ap-bar-cell">
                            <div className="ap-bar ap-bar-sm">
                              <i
                                style={{
                                  width: `${e.avgScore * 10}%`,
                                  background: col,
                                }}
                              />
                            </div>
                            <b>{e.avgScore}</b>
                          </div>
                        </td>
                        <td className="hide-md">
                          <span className="ap-muted">
                            {PARAM_LABELS[lowest]}
                          </span>
                        </td>
                        <td className="hide-sm">
                          {responded}/{d.qIndex + 1} cycles
                        </td>
                        <td className="hide-sm">{d.quarter}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              page={Math.min(page, pages)}
              pages={pages}
              onPage={setPage}
              from={start + 1}
              to={start + slice.length}
              total={rows.length}
            />
          </>
        )}
        <PrivacyNote>
          Employee-level information is restricted to authorized HR users. All
          names and scores are fictional sample data.
        </PrivacyNote>
      </section>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: CRITICAL TALENT
--------------------------------------------------------------- */
function TalentMatrix({ items, onSelect, quad, onQuad }) {
  const X = (r) => ((Math.min(90, Math.max(10, r)) - 10) / 80) * 100;
  const Y = (c) => ((Math.min(100, Math.max(30, c)) - 30) / 70) * 100;
  const sx = X(55);
  const sy = Y(62);
  const rects = {
    keep: {
      left: 0,
      bottom: `${sy}%`,
      width: `${sx}%`,
      height: `${100 - sy}%`,
    },
    protect: {
      left: `${sx}%`,
      bottom: `${sy}%`,
      width: `${100 - sx}%`,
      height: `${100 - sy}%`,
    },
    stable: { left: 0, bottom: 0, width: `${sx}%`, height: `${sy}%` },
    watch: {
      left: `${sx}%`,
      bottom: 0,
      width: `${100 - sx}%`,
      height: `${sy}%`,
    },
  };
  return (
    <div className="ap-matrix">
      <div className="ap-matrix-y">
        <span>High criticality</span>
        <span>Lower criticality</span>
      </div>
      <div className="ap-matrix-plot">
        {Object.keys(rects).map((k) => {
          const n = items.filter((e) => e.quadrant === k).length;
          return (
            <button
              key={k}
              type="button"
              aria-pressed={quad === k}
              className={`ap-quad tone-${QUADRANTS[k].tone} ${quad === k ? "is-on" : ""}`}
              style={rects[k]}
              onClick={() => onQuad(quad === k ? "all" : k)}
            >
              <span className="ap-quad-label">
                <b>{QUADRANTS[k].title}</b>
                <em>{n}</em>
              </span>
            </button>
          );
        })}
        {items.map((e) => {
          const r = mulberry32(hashId(e.id));
          const jx = (r() - 0.5) * 2.4;
          const jy = (r() - 0.5) * 2.4;
          const dim = quad !== "all" && e.quadrant !== quad;
          return (
            <button
              key={e.id}
              type="button"
              className={`ap-node tone-${QUADRANTS[e.quadrant].tone} ${dim ? "is-dim" : ""}`}
              style={{
                left: `${Math.min(98, Math.max(2, X(e.risk) + jx))}%`,
                bottom: `${Math.min(97, Math.max(3, Y(e.criticality) + jy))}%`,
              }}
              onClick={() => onSelect(e)}
              title={`${e.name} · ${e.role}`}
              aria-label={`${e.name}, ${e.role}, ${QUADRANTS[e.quadrant].title}`}
            >
              {initials(e.name)}
            </button>
          );
        })}
      </div>
      <div className="ap-matrix-x">
        <span>Lower retention risk</span>
        <span>Higher retention risk →</span>
      </div>
    </div>
  );
}

function TalentView() {
  const d = useData();
  const { active, resigned } = d.talent;
  const [quad, setQuad] = useState("protect");
  const [role, setRole] = useState("All roles");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("risk");
  const [page, setPage] = useState(1);
  const loading = useBriefLoading([d.quarter, d.unit, d.dept], 320);

  const roles = useMemo(
    () => ["All roles", ...[...new Set(active.map((e) => e.role))].sort()],
    [active],
  );
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return active.filter(
      (e) =>
        (role === "All roles" || e.role === role) &&
        (!s ||
          `${e.name} ${e.role} ${e.department} ${e.whyCritical}`
            .toLowerCase()
            .includes(s)),
    );
  }, [active, role, q]);
  const rows = useMemo(() => {
    const r = visible.filter((e) => quad === "all" || e.quadrant === quad);
    return r
      .slice()
      .sort((a, b) =>
        sort === "name" ? a.name.localeCompare(b.name) : b.risk - a.risk,
      );
  }, [visible, quad, sort]);
  useEffect(() => setPage(1), [quad, role, q, sort, d.quarter, d.unit, d.dept]);
  const pages = Math.max(1, Math.ceil(rows.length / 8));
  const pg = Math.min(page, pages);
  const start = (pg - 1) * 8;
  const slice = rows.slice(start, start + 8);
  const count = (k) => visible.filter((e) => e.quadrant === k).length;

  const critical = visible.filter((e) => e.highCriticality);
  const cont = { Ready: 0, Partial: 0, None: 0 };
  critical.forEach((e) => (cont[e.successor] += 1));
  const ctotal = critical.length || 1;
  const gaps = critical
    .filter((e) => e.successor === "None")
    .sort((a, b) => b.risk - a.risk)
    .slice(0, 4);

  return (
    <div className="ap-view">
      <PageHeader
        eyebrow="Talent protection"
        title="Critical Talent Pool"
        subtitle="Hard-to-replace colleagues, retention risk and continuity cover in one view."
      >
        <FilterBar />
      </PageHeader>

      <section className="ap-kpi-grid ap-kpi-grid-quad">
        {Object.keys(QUADRANTS).map((k) => (
          <button
            key={k}
            type="button"
            className={`ap-quad-kpi tone-${QUADRANTS[k].tone} ${quad === k ? "is-on" : ""}`}
            onClick={() => setQuad(k)}
          >
            <span className="ap-quad-kpi-title">{QUADRANTS[k].title}</span>
            <span className="ap-quad-kpi-num">{loading ? "–" : count(k)}</span>
            <span className="ap-quad-kpi-hint">{QUADRANTS[k].hint}</span>
          </button>
        ))}
      </section>

      <div className="ap-grid-8-4">
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Talent exposure matrix</h2>
              <p>
                Each node is a critical colleague · select a node for their
                profile, a quadrant to filter
              </p>
            </div>
            <Tip text="Risk threshold at 55/100 and criticality threshold at 62/100 separate the four quadrants." />
          </div>
          {loading ? (
            <Skeleton h={380} r={10} />
          ) : visible.length ? (
            <TalentMatrix
              items={visible}
              onSelect={(e) => d.openPerson(e)}
              quad={quad}
              onQuad={setQuad}
            />
          ) : (
            <EmptyState
              icon={ShieldCheck}
              title="No critical talent in this view"
              text="Adjust the business unit or function filters."
            />
          )}
          {resigned > 0 && (
            <p className="ap-card-foot">
              {resigned} critical colleague{resigned === 1 ? " has" : "s have"}{" "}
              already left in this cycle and are not shown.
            </p>
          )}
        </section>

        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Continuity planning</h2>
              <p>Backup readiness for high-criticality roles</p>
            </div>
          </div>
          <div className="ap-cont-bar" aria-hidden="true">
            <i
              className="tone-positive"
              style={{ width: `${(cont.Ready / ctotal) * 100}%` }}
            />
            <i
              className="tone-warning"
              style={{ width: `${(cont.Partial / ctotal) * 100}%` }}
            />
            <i
              className="tone-critical"
              style={{ width: `${(cont.None / ctotal) * 100}%` }}
            />
          </div>
          <ul className="ap-cont-list">
            {[
              ["Ready", "Backup in place"],
              ["Partial", "Backup partially ready"],
              ["None", "No backup identified"],
            ].map(([k, l]) => (
              <li key={k}>
                <Continuity level={k} showLabel={false} />
                <span>{l}</span>
                <b>{cont[k]}</b>
              </li>
            ))}
          </ul>
          <h3 className="ap-subhead">Roles needing a continuity plan</h3>
          {gaps.length ? (
            <ul className="ap-gap-list">
              {gaps.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => d.openPerson(e)}>
                    <Avatar
                      name={e.name}
                      size={30}
                      tone={QUADRANTS[e.quadrant].tone}
                    />
                    <div>
                      <b>{e.role}</b>
                      <small>{e.department}</small>
                    </div>
                    <ChevronRight size={14} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ap-body ap-muted">
              Every high-criticality role currently has some backup cover.
            </p>
          )}
        </section>
      </div>

      <section className="ap-card ap-card-flush">
        <div className="ap-toolbar">
          <div
            className="ap-chip-filters"
            role="group"
            aria-label="Quadrant filter"
          >
            {[...Object.keys(QUADRANTS), "all"].map((k) => (
              <button
                key={k}
                type="button"
                className={quad === k ? "is-on" : ""}
                onClick={() => setQuad(k)}
              >
                {k === "all" ? "All" : QUADRANTS[k].title}
                <em>{k === "all" ? visible.length : count(k)}</em>
              </button>
            ))}
          </div>
          <label className="ap-search">
            <Search size={15} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or role"
              aria-label="Search talent"
            />
          </label>
          <Select
            label="Role"
            value={roles.includes(role) ? role : "All roles"}
            onChange={setRole}
            options={roles}
          />
          <Select
            label="Sort by"
            value={sort === "name" ? "Name" : "Retention risk"}
            onChange={(v) => setSort(v === "Name" ? "name" : "risk")}
            options={["Retention risk", "Name"]}
          />
          <button
            type="button"
            className="ap-btn ap-btn-dark"
            disabled={!rows.length}
            onClick={() => exportTalentCsv(rows, d.quarter)}
          >
            <Download size={14} /> Export
          </button>
        </div>
        {loading ? (
          <div className="ap-skel-rows">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} h={58} r={8} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No one in this group"
            text="Try another quadrant, role or search term."
            action="Show all"
            onAction={() => {
              setQuad("all");
              setRole("All roles");
              setQ("");
            }}
          />
        ) : (
          <>
            <ul className="ap-roster">
              {slice.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => d.openPerson(e)}>
                    <Avatar name={e.name} tone={QUADRANTS[e.quadrant].tone} />
                    <div className="ap-roster-main">
                      <b>{e.name}</b>
                      <small>
                        <Briefcase size={12} /> {e.role} · {e.department}
                      </small>
                      <small>
                        <Star size={12} /> {e.whyCritical}
                      </small>
                    </div>
                    <Continuity level={e.successor} />
                    <RiskBand bucket={e.bucket} score={e.risk} />
                    <ChevronRight size={16} className="ap-roster-go" />
                  </button>
                </li>
              ))}
            </ul>
            <Pager
              page={pg}
              pages={pages}
              onPage={setPage}
              from={start + 1}
              to={start + slice.length}
              total={rows.length}
            />
          </>
        )}
        <PrivacyNote>
          Talent information is restricted to authorized HR users. Continuity
          status shows readiness only - no replacement details are stored here.
        </PrivacyNote>
      </section>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: DEPARTMENTS
--------------------------------------------------------------- */
function DepartmentDetail({ name, onBack }) {
  const d = useData();
  const bundle = useMemo(() => {
    const snaps = QUARTERS.map((_, i) =>
      d.unitOnly(d.getSnap(i)).filter((e) => e.department === name),
    );
    const series = snaps.map((sn, i) => ({ q: QUARTERS[i], ...stats(sn) }));
    const now = snaps[d.qIndex].map((e) => {
      const risk = computeRisk(e.params, d.weights);
      return { ...e, risk, bucket: riskBucket(risk) };
    });
    const g = { Retained: [], "At risk": [], Resigned: [] };
    now.forEach((e) => g[e.status].push(e));
    const a = driverAvg(g.Retained),
      b = driverAvg(g["At risk"]),
      c = driverAvg(g.Resigned);
    const drivers = PARAM_KEYS.map((k) => ({
      key: k,
      parameter: PARAM_LABELS[k],
      Retained: a[k],
      "At risk": b[k],
      Resigned: c[k],
    }));
    const attention = now
      .filter((e) => e.bucket === "High" && e.status !== "Resigned")
      .sort((x, y) => y.risk - x.risk)
      .slice(0, 6);
    return {
      series,
      cur: series[d.qIndex],
      prev: d.qIndex > 0 ? series[d.qIndex - 1] : null,
      drivers,
      attention,
    };
  }, [name, d.qIndex, d.weights, d.getSnap, d.unitOnly]);
  const { cur, prev } = bundle;
  return (
    <div className="ap-view">
      <button type="button" className="ap-back" onClick={onBack}>
        <ChevronLeft size={15} /> All functions
      </button>
      <PageHeader
        eyebrow="Department intelligence"
        title={name}
        subtitle={`${cur.total} employees · survey cycle ${d.quarter}`}
      >
        <FilterBar show={["quarter", "unit"]} />
      </PageHeader>
      <section className="ap-kpi-grid">
        <Kpi
          accent
          label="Retention rate"
          value={cur.rate}
          decimals={1}
          unit="%"
          delta={prev ? cur.rate - prev.rate : null}
          deltaProps={{ unit: " pts" }}
          caption="vs previous cycle"
          spark={bundle.series.map((s) => s.rate)}
          sparkColor={THEME.retained}
        />
        <Kpi
          label="Employees at risk"
          value={cur.atRisk}
          unit=" people"
          delta={prev ? cur.atRisk - prev.atRisk : null}
          deltaProps={{ goodWhen: "down", digits: 0 }}
          caption={`${cur.total ? ((cur.atRisk / cur.total) * 100).toFixed(1) : 0}% of function`}
          spark={bundle.series.map((s) => s.atRisk)}
          sparkColor={THEME.risk}
        />
        <Kpi
          label="Resignations"
          value={cur.resigned}
          unit=" people"
          delta={prev ? cur.resigned - prev.resigned : null}
          deltaProps={{ goodWhen: "down", digits: 0 }}
          caption="this cycle"
          spark={bundle.series.map((s) => s.resigned)}
          sparkColor={THEME.resigned}
        />
        <Kpi
          label="Engagement score"
          value={cur.avg}
          decimals={1}
          unit="/10"
          delta={prev ? cur.avg - prev.avg : null}
          deltaProps={{}}
          caption="average survey score"
          spark={bundle.series.map((s) => s.avg)}
        />
      </section>
      <div className="ap-grid-8-4">
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Retention trend</h2>
              <p>{name} across survey cycles</p>
            </div>
          </div>
          <TrendChart
            data={bundle.series}
            quarter={d.quarter}
            onQuarter={d.setQuarter}
          />
        </section>
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Where support may help</h2>
              <p>Highest workforce-risk scores</p>
            </div>
          </div>
          {bundle.attention.length ? (
            <ul className="ap-gap-list">
              {bundle.attention.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => d.openPerson(e)}>
                    <Avatar name={e.name} size={30} />
                    <div>
                      <b>{e.name}</b>
                      <small>{e.role}</small>
                    </div>
                    <RiskBand bucket={e.bucket} score={e.risk} compact />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ap-body ap-muted">
              No employees currently require attention in this function.
            </p>
          )}
        </section>
      </div>
      <section className="ap-card">
        <div className="ap-card-head">
          <div>
            <h2>Retention drivers</h2>
            <p>Retained vs at-risk vs resigned in {name}</p>
          </div>
        </div>
        <DriverComparison data={bundle.drivers} />
      </section>
    </div>
  );
}

function DepartmentsView() {
  const d = useData();
  const loading = useBriefLoading([d.quarter, d.unit], 320);
  if (d.deptDetail)
    return (
      <DepartmentDetail
        name={d.deptDetail}
        onBack={() => d.setDeptDetail(null)}
      />
    );
  const rows = d.deptRows.filter((r) => r.s.total > 0);
  return (
    <div className="ap-view">
      <PageHeader
        eyebrow="Organizational insights"
        title="Departments"
        subtitle="Compare functions on retention, workforce risk and engagement. Select a function for detail."
      >
        <FilterBar show={["quarter", "unit"]} />
      </PageHeader>
      {loading ? (
        <div className="ap-dept-grid">
          {Array.from({ length: 6 }, (_, i) => (
            <div className="ap-card" key={i}>
              <Skeleton h={160} r={8} />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <section className="ap-card">
          <EmptyState
            icon={Building2}
            title="No headcount in this business unit"
            text="Choose another business unit to see function-level results."
            action="Show all units"
            onAction={() => d.setUnit(ALL_UNITS)}
          />
        </section>
      ) : (
        <div className="ap-dept-grid">
          {rows.map((r) => {
            const st = deptStatus(r.s.rate, r.atRiskPct);
            const color =
              r.s.rate >= 90
                ? THEME.retained
                : r.s.rate >= 85
                  ? THEME.risk
                  : THEME.resigned;
            return (
              <button
                key={r.name}
                type="button"
                className="ap-dept-card"
                onClick={() => d.setDeptDetail(r.name)}
              >
                <div className="ap-dept-top">
                  <div>
                    <h3>{r.name}</h3>
                    <small>{r.s.total} employees</small>
                  </div>
                  <span className={`ap-pill tone-${st.tone}`}>{st.label}</span>
                </div>
                <div className="ap-dept-main">
                  <div className="ap-dept-rate" style={{ color }}>
                    {r.s.rate.toFixed(1)}
                    <span>%</span>
                  </div>
                  <Sparkline data={r.rateSeries} color={color} w={92} h={34} />
                </div>
                <div className="ap-dept-sub">
                  <Delta value={r.delta} unit=" pts" />{" "}
                  <span>retention vs prior cycle</span>
                </div>
                <dl className="ap-dept-stats">
                  <div>
                    <dt>At risk</dt>
                    <dd>{r.atRiskPct.toFixed(1)}%</dd>
                  </div>
                  <div>
                    <dt>Resignations</dt>
                    <dd>
                      {r.s.resigned}{" "}
                      <Sparkline
                        data={r.resignedSeries}
                        color={THEME.resigned}
                        w={40}
                        h={16}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>Engagement</dt>
                    <dd>{r.s.avg}/10</dd>
                  </div>
                </dl>
                <div className="ap-dept-drivers">
                  <div>
                    <small>Strongest driver</small>
                    <b className="is-pos">
                      {PARAM_LABELS[r.strongest.key]} · {r.strongest.val}
                    </b>
                  </div>
                  <div>
                    <small>Weakest driver</small>
                    <b className="is-neg">
                      {PARAM_LABELS[r.weakest.key]} · {r.weakest.val}
                    </b>
                  </div>
                </div>
                <span className="ap-dept-go">
                  View analysis <ArrowRight size={14} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: SURVEY CYCLES
--------------------------------------------------------------- */
function CyclesView() {
  const d = useData();
  const [sel, setSel] = useState(QUARTERS.length - 1);
  const cycles = QUARTERS.map((q, i) => {
    const snap = d.getSnap(i);
    const s = stats(snap);
    const part = SURVEY_PARTICIPATION[i];
    return {
      i,
      q,
      s,
      part,
      completion: Math.max(70, part - (2 + (i % 3))),
      status: i === QUARTERS.length - 1 ? "Active" : "Closed",
      prevRate: i > 0 ? stats(d.getSnap(i - 1)).rate : null,
    };
  });
  const c = cycles[sel];
  return (
    <div className="ap-view">
      <PageHeader
        eyebrow="Employee listening"
        title="Survey Cycles"
        subtitle="Quarterly pulse surveys, with participation and headline results for each cycle."
      />
      <section className="ap-timeline" aria-label="Survey cycles">
        <div className="ap-timeline-line" aria-hidden="true" />
        {cycles.map((x) => (
          <button
            key={x.q}
            type="button"
            className={`ap-cycle ${sel === x.i ? "is-on" : ""}`}
            onClick={() => setSel(x.i)}
            aria-pressed={sel === x.i}
          >
            <span
              className={`ap-cycle-dot ${x.status === "Active" ? "is-active" : ""}`}
            />
            <span className="ap-cycle-q">{x.q}</span>
            <span className="ap-cycle-name">Pulse Survey</span>
            <span
              className={`ap-pill tone-${x.status === "Active" ? "positive" : "neutral"}`}
            >
              {x.status}
            </span>
            <span className="ap-cycle-part">
              {x.part}%<small>participation</small>
            </span>
            <span className="ap-bar ap-bar-sm">
              <i
                style={{
                  width: `${x.part}%`,
                  background: x.part < 88 ? THEME.risk : THEME.retained,
                }}
              />
            </span>
          </button>
        ))}
      </section>
      <section className="ap-card">
        <div className="ap-card-head">
          <div>
            <h2>Pulse Survey · {c.q}</h2>
            <p>
              {c.status === "Active"
                ? "Currently active cycle"
                : "Closed cycle"}{" "}
              · {c.s.total} employees surveyed
            </p>
          </div>
          <button
            type="button"
            className="ap-btn ap-btn-dark"
            onClick={() => {
              d.setQuarter(c.q);
              d.go("dashboard");
            }}
          >
            Open in Retention Analysis <ArrowRight size={14} />
          </button>
        </div>
        <div className="ap-metric-grid">
          <div>
            <small>Participation</small>
            <b>{c.part}%</b>
            <Delta
              value={c.i > 0 ? c.part - SURVEY_PARTICIPATION[c.i - 1] : null}
              unit=" pts"
            />
          </div>
          <div>
            <small>Employees surveyed</small>
            <b>{c.s.total}</b>
          </div>
          <div>
            <small>Completion rate</small>
            <b>{c.completion}%</b>
          </div>
          <div>
            <small>Retention rate</small>
            <b>{c.s.rate.toFixed(1)}%</b>
            <Delta
              value={c.prevRate != null ? c.s.rate - c.prevRate : null}
              unit=" pts"
            />
          </div>
          <div>
            <small>Resignations</small>
            <b>{c.s.resigned}</b>
          </div>
          <div>
            <small>Average score</small>
            <b>{c.s.avg}/10</b>
          </div>
        </div>
        {c.part < 88 && (
          <div className="ap-callout">
            <Info size={16} />
            <p>
              Participation below 88% is worth following up. A quiet drop in
              response can be an earlier signal than the scores themselves.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: TAKE SURVEY
--------------------------------------------------------------- */
function RatingScale({ value, onChange, label }) {
  const refs = useRef([]);
  const move = (e, n) => {
    let t = null;
    if (e.key === "ArrowRight" || e.key === "ArrowUp")
      t = Math.min(10, (value || n) + 1);
    if (e.key === "ArrowLeft" || e.key === "ArrowDown")
      t = Math.max(1, (value || n) - 1);
    if (t) {
      e.preventDefault();
      onChange(t);
      refs.current[t - 1]?.focus();
    }
  };
  return (
    <div role="radiogroup" aria-label={label} className="ap-rating">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          ref={(el) => (refs.current[n - 1] = el)}
          type="button"
          role="radio"
          aria-checked={value === n}
          tabIndex={value === n || (!value && n === 1) ? 0 : -1}
          className={`ap-rating-btn ${value === n ? "is-on" : ""}`}
          onClick={() => onChange(n)}
          onKeyDown={(e) => move(e, n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function SurveyView() {
  const d = useData();
  const [step, setStep] = useState(0);
  const [qi, setQi] = useState(QUARTERS.length - 1);
  const [name, setName] = useState("");
  const [dept, setDept] = useState(DEPTS[0].name);
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(PARAM_KEYS.map((k) => [k, null])),
  );
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const total = PARAM_KEYS.length + 2;
  const key =
    step >= 1 && step <= PARAM_KEYS.length ? PARAM_KEYS[step - 1] : null;
  const val = key ? answers[key] : null;
  const progress = Math.min(100, (step / (total - 1)) * 100);

  const next = () => {
    if (step === 0 && !name.trim())
      return setErr(
        "Please enter your name so we can avoid duplicate responses.",
      );
    if (key && !val) return setErr("Choose a rating to continue.");
    setErr("");
    setStep(step + 1);
  };
  const submit = async () => {
    setBusy(true);
    const avg =
      PARAM_KEYS.reduce((s, k) => s + answers[k], 0) / PARAM_KEYS.length;
    await d.submitSurvey(
      {
        id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: name.trim(),
        department: dept,
        params: { ...answers },
        avgScore: Math.round(avg * 10) / 10,
        status: statusFromAvg(avg),
        comment: comment.trim() || null,
        source: "live",
        submittedAt: new Date().toISOString(),
      },
      qi,
    );
    setBusy(false);
    setStep(total);
  };
  const reset = () => {
    setStep(0);
    setName("");
    setComment("");
    setErr("");
    setAnswers(Object.fromEntries(PARAM_KEYS.map((k) => [k, null])));
  };
  const reflect = (v) =>
    v <= 4
      ? "Thank you for being candid - this is exactly what helps us improve."
      : v <= 7
        ? "Thanks. Mixed experiences are valuable to hear."
        : "Glad to hear it. Thank you.";

  return (
    <div className="ap-survey">
      <div className="ap-survey-top">
        <Wordmark compact />
        <span>Employee listening survey · {QUARTERS[qi]}</span>
      </div>
      {step < total && (
        <div
          className="ap-survey-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <i style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="ap-survey-stage" key={step}>
        {step === 0 && (
          <div className="ap-survey-card">
            <div className="ap-eyebrow">Employee listening</div>
            <h1>Your voice helps shape Ashtavinayak.</h1>
            <p className="ap-survey-lead">
              Seven short questions, about three minutes. There are no right or
              wrong answers - honest feedback is the most useful kind.
            </p>
            <div className="ap-survey-fields">
              <label className="ap-field">
                <span>Your name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  autoComplete="name"
                />
              </label>
              <Select
                label="Function"
                value={dept}
                onChange={setDept}
                options={DEPTS.map((x) => x.name)}
              />
              <Select
                label="Survey cycle"
                value={QUARTERS[qi]}
                onChange={(v) => setQi(QUARTERS.indexOf(v))}
                options={QUARTERS}
              />
            </div>
            <PrivacyNote>
              Responses are seen only by authorized HR users and are used to
              improve the workplace.
            </PrivacyNote>
          </div>
        )}
        {key && (
          <div className="ap-survey-card">
            <div className="ap-eyebrow">
              {SURVEY_COPY[key].eyebrow} · Question {step} of{" "}
              {PARAM_KEYS.length}
            </div>
            <h1 className="ap-survey-q">{SURVEY_COPY[key].q}</h1>
            <p className="ap-survey-lead">{SURVEY_COPY[key].hint}</p>
            <RatingScale
              value={val}
              onChange={(v) => {
                setAnswers({ ...answers, [key]: v });
                setErr("");
              }}
              label={SURVEY_COPY[key].q}
            />
            <div className="ap-rating-anchors">
              <span>Strongly disagree</span>
              <span>Strongly agree</span>
            </div>
            <p className="ap-survey-reflect" aria-live="polite">
              {val ? reflect(val) : "\u00A0"}
            </p>
          </div>
        )}
        {step === PARAM_KEYS.length + 1 && (
          <div className="ap-survey-card">
            <div className="ap-eyebrow">Almost done</div>
            <h1 className="ap-survey-q">
              Is there anything else you would like to share?
            </h1>
            <p className="ap-survey-lead">
              Optional. Please avoid sensitive personal details.
            </p>
            <textarea
              className="ap-textarea"
              rows={5}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts"
            />
          </div>
        )}
        {step === total && (
          <div className="ap-survey-card is-center">
            <span className="ap-done-check">
              <Check size={30} />
            </span>
            <h1>Thank you.</h1>
            <p className="ap-survey-lead">
              Your response for {QUARTERS[qi]} has been recorded and will be
              reflected in the retention analysis.
            </p>
            <div className="ap-survey-actions is-center">
              <button
                type="button"
                className="ap-btn ap-btn-primary"
                onClick={() => {
                  d.setQuarter(QUARTERS[qi]);
                  d.go("dashboard");
                }}
              >
                View retention analysis
              </button>
              <button
                type="button"
                className="ap-btn ap-btn-ghost"
                onClick={reset}
              >
                Submit another response
              </button>
            </div>
          </div>
        )}
        {err && (
          <div className="ap-form-err ap-survey-err" role="alert">
            {err}
          </div>
        )}
        {step < total && (
          <div className="ap-survey-actions">
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              disabled={step === 0}
              onClick={() => {
                setErr("");
                setStep(step - 1);
              }}
            >
              Back
            </button>
            {step <= PARAM_KEYS.length ? (
              <button
                type="button"
                className="ap-btn ap-btn-primary"
                onClick={next}
              >
                {step === 0 ? "Begin" : "Continue"} <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                className="ap-btn ap-btn-primary"
                disabled={busy}
                onClick={submit}
              >
                {busy ? "Submitting…" : "Submit response"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   VIEW: MODEL SETTINGS
--------------------------------------------------------------- */
const WEIGHT_COLORS = [
  "#26272b",
  "#f26232",
  "#7a7e90",
  "#c8ccd9",
  "#21865A",
  "#C58A00",
  "#a5a9ba",
];
function SettingsView() {
  const d = useData();
  const { weights, setWeights } = d;
  const total = PARAM_KEYS.reduce((s, k) => s + weights[k], 0);
  const preview = useMemo(() => {
    const count = (w) => {
      const c = { High: 0, Medium: 0, Low: 0 };
      d.filtered.forEach((e) => (c[riskBucket(computeRisk(e.params, w))] += 1));
      return c;
    };
    return { base: count(DEFAULT_WEIGHTS), now: count(weights) };
  }, [d.filtered, weights]);
  const changed = PARAM_KEYS.some((k) => weights[k] !== DEFAULT_WEIGHTS[k]);
  const normalize = () => {
    const t = total || 1;
    const n = {};
    PARAM_KEYS.forEach((k) => (n[k] = Math.round((weights[k] / t) * 100)));
    setWeights(n);
  };
  return (
    <div className="ap-view">
      <PageHeader
        eyebrow="Administration"
        title="Model Settings"
        subtitle="Set how much each retention driver contributes to the workforce risk score."
      >
        <span className="ap-pill tone-neutral">
          <Lock size={12} /> People Analytics administrators
        </span>
      </PageHeader>
      <div className="ap-grid-8-4">
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Retention driver weights</h2>
              <p>
                Drag a slider or enter a value. Weights are applied
                proportionally.
              </p>
            </div>
          </div>
          <div className="ap-wbar" aria-hidden="true">
            {PARAM_KEYS.map((k, i) => (
              <i
                key={k}
                title={`${PARAM_LABELS[k]} ${weights[k]}`}
                style={{
                  width: `${total ? (weights[k] / total) * 100 : 0}%`,
                  background: WEIGHT_COLORS[i],
                }}
              />
            ))}
          </div>
          {PARAM_KEYS.map((k, i) => (
            <div className="ap-weight" key={k}>
              <div className="ap-weight-label">
                <i style={{ background: WEIGHT_COLORS[i] }} />
                <div>
                  <b>{PARAM_LABELS[k]}</b>
                  <small>{PARAM_DESC[k]}</small>
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={40}
                value={weights[k]}
                onChange={(e) =>
                  setWeights({ ...weights, [k]: Number(e.target.value) })
                }
                aria-label={`${PARAM_LABELS[k]} weight`}
              />
              <label className="ap-weight-num">
                <input
                  type="number"
                  min={0}
                  max={40}
                  value={weights[k]}
                  onChange={(e) =>
                    setWeights({
                      ...weights,
                      [k]: Math.max(
                        0,
                        Math.min(40, Number(e.target.value) || 0),
                      ),
                    })
                  }
                />
                <span>%</span>
              </label>
            </div>
          ))}
          <div className="ap-weight-foot">
            <div className={`ap-total ${total === 100 ? "is-ok" : "is-warn"}`}>
              Total weight <b>{total}%</b>
              {total !== 100 && (
                <span>- scores are normalized, but 100% is recommended</span>
              )}
            </div>
            <div className="ap-weight-actions">
              <button
                type="button"
                className="ap-btn ap-btn-dark"
                onClick={normalize}
              >
                Normalize to 100%
              </button>
              <button
                type="button"
                className="ap-btn ap-btn-ghost"
                onClick={() => setWeights(DEFAULT_WEIGHTS)}
                disabled={!changed}
              >
                <RotateCcw size={14} /> Reset to default
              </button>
            </div>
          </div>
        </section>
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Impact preview</h2>
              <p>
                {d.quarter} · {d.filtered.length} employees in view
              </p>
            </div>
          </div>
          {["High", "Medium", "Low"].map((b) => {
            const diff = preview.now[b] - preview.base[b];
            return (
              <div className="ap-impact" key={b}>
                <div className="ap-impact-top">
                  <span>
                    <i style={{ background: RISK_META[b].color }} />
                    {RISK_META[b].label}
                  </span>
                  <b>{preview.now[b]}</b>
                </div>
                <div className="ap-bar">
                  <i
                    style={{
                      width: `${d.filtered.length ? (preview.now[b] / d.filtered.length) * 100 : 0}%`,
                      background: RISK_META[b].color,
                    }}
                  />
                </div>
                <small>
                  Default weights: {preview.base[b]}{" "}
                  {diff !== 0 && (
                    <em className={diff > 0 ? "is-up" : "is-down"}>
                      ({diff > 0 ? "+" : ""}
                      {diff})
                    </em>
                  )}
                </small>
              </div>
            );
          })}
          <p className="ap-card-foot">
            Changes apply immediately to workforce risk, the Employees list and
            the Critical Talent Pool. Nothing is saved to a production system in
            this prototype.
          </p>
        </section>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   RETENTION WATCH - early-warning signals scored from monthly HR
   data (attendance, engagement, performance, workload, growth, pay).
   Styles: RetentionWatch.css (rw-* classes); everything else reuses
   the dashboard's ap-* components.
--------------------------------------------------------------- */
const RW_SIGNALS = [
  {
    k: "eng",
    label: "Engagement drop",
    w: 20,
    why: "Pulse score fell or sits below 55",
    act: "Hold a stay interview within two weeks; ask what changed.",
  },
  {
    k: "abs",
    label: "Unplanned absence",
    w: 14,
    why: "Unplanned leave days in last 90 days",
    act: "Manager check-in on wellbeing; offer EAP and flexible hours.",
  },
  {
    k: "perf",
    label: "Performance dip",
    w: 14,
    why: "Rating fell since last review cycle",
    act: "Clarify goals and blockers; avoid jumping to a PIP.",
  },
  {
    k: "pay",
    label: "Below-market pay",
    w: 12,
    why: "Compa-ratio under 0.95",
    act: "Run a comp review against current market bands.",
  },
  {
    k: "ot",
    label: "Sustained overtime",
    w: 10,
    why: "Average weekly hours above 42",
    act: "Review workload and staffing; redistribute or backfill.",
  },
  {
    k: "stag",
    label: "Career stagnation",
    w: 10,
    why: "Over 2 years since last promotion",
    act: "Discuss growth path, stretch projects or an internal move.",
  },
  {
    k: "oo",
    label: "Missed 1:1s",
    w: 10,
    why: "1:1s cancelled in last 8 weeks",
    act: "Reinstate a regular 1:1 cadence with the manager.",
  },
  {
    k: "pto",
    label: "Leave not taken",
    w: 5,
    why: "Unused annual leave days (burnout risk)",
    act: "Encourage planned time off this quarter.",
  },
  {
    k: "mgr",
    label: "Manager change",
    w: 5,
    why: "New manager in last 6 months",
    act: "Arrange a skip-level conversation and onboarding to new manager.",
  },
];
const RW_COLS = [
  "id",
  "name",
  "dept",
  "role",
  "manager",
  "tenure_months",
  "absence_days_90",
  "engagement",
  "engagement_prev",
  "perf",
  "perf_prev",
  "overtime_hrs_wk",
  "months_since_promo",
  "compa_ratio",
  "oneonones_missed",
  "pto_unused_days",
  "manager_changed",
];
const RW_TEXT = ["id", "name", "dept", "role", "manager"];
const RW_ID_RE = /^[A-Za-z0-9_.~:@+-]{1,60}$/;
const RW_STORE_KEY = "retention-watch:employees";

const RW_SOURCES = [
  {
    k: "hrms",
    name: "HRMS",
    sub: "Employee master",
    freq: "Monthly",
    owner: "HR operations",
    eg: "SAP SuccessFactors, Darwinbox, Keka, Zoho People",
    hue: "#3b7dd8",
  },
  {
    k: "att",
    name: "Attendance & leave",
    sub: "Biometric / leave module",
    freq: "Monthly",
    owner: "HR ops / payroll",
    eg: "Biometric system, greytHR, HRMS leave module",
    hue: "#0f8b8d",
  },
  {
    k: "time",
    name: "Timesheets",
    sub: "Time / project tracking",
    freq: "Monthly",
    owner: "PMO / team leads",
    eg: "Jira Tempo, Zoho Projects, Harvest",
    hue: "#8a6bd1",
  },
  {
    k: "survey",
    name: "Engagement survey",
    sub: "Quarterly pulse tool",
    freq: "Quarterly",
    owner: "HRBP / people analytics",
    eg: "Culture Amp, Qualtrics, Microsoft Forms",
    hue: "#d9771c",
  },
  {
    k: "perf",
    name: "Performance system",
    sub: "Review cycles",
    freq: "Each cycle",
    owner: "HRBP",
    eg: "Lattice, SuccessFactors PMS, Keka PMS",
    hue: "#c4362b",
  },
  {
    k: "pay",
    name: "Payroll & pay bands",
    sub: "Salary benchmarking",
    freq: "Quarterly",
    owner: "Compensation team",
    eg: "Payroll register + salary band sheet",
    hue: "#3f8f5a",
  },
  {
    k: "mgr",
    name: "Manager 1:1 log",
    sub: "Calendar / check-ins",
    freq: "Monthly",
    owner: "Line managers",
    eg: "Outlook or Google Calendar, check-in tool",
    hue: "#a0782a",
  },
];
const RW_SRC = Object.fromEntries(RW_SOURCES.map((s) => [s.k, s]));
const RW_FIELDMAP = [
  {
    f: "Employee ID, name, role",
    src: "hrms",
    where: "Employee profile / master report",
    step: 0,
  },
  {
    f: "Department & reporting manager",
    src: "hrms",
    where: "Org structure report",
    step: 0,
  },
  { f: "Tenure", src: "hrms", where: "Date of joining", step: 0 },
  {
    f: "Unplanned leave days (last 90 days)",
    src: "att",
    where: "Leave register: sick + unplanned casual leave",
    step: 1,
  },
  {
    f: "Unused annual leave",
    src: "att",
    where: "Leave balance report",
    step: 1,
  },
  {
    f: "Engagement score now & last quarter",
    src: "survey",
    where: "Individual or team pulse results (0–100)",
    step: 2,
  },
  {
    f: "Performance rating now & previous",
    src: "perf",
    where: "Final calibrated rating per cycle (1–5)",
    step: 3,
  },
  {
    f: "Average hours per week",
    src: "time",
    where: "Logged hours ÷ weeks, last 8 weeks",
    step: 4,
  },
  {
    f: "1:1s missed (last 8 weeks)",
    src: "mgr",
    where: "Cancelled or skipped recurring 1:1s",
    step: 4,
  },
  {
    f: "Manager changed in last 6 months",
    src: "hrms",
    where: "Reporting-line change history",
    step: 4,
  },
  {
    f: "Months since last promotion",
    src: "hrms",
    where: "Job history: last grade or title change",
    step: 5,
  },
  {
    f: "Compa-ratio (salary ÷ band midpoint)",
    src: "pay",
    where: "Monthly gross from payroll ÷ band midpoint",
    step: 5,
  },
];

const rwClamp = (x) => Math.max(0, Math.min(1, x));
function rwSeverity(e, prev) {
  const eng = prev ? e.engagement_prev : e.engagement;
  const perf = prev ? e.perf_prev : e.perf;
  const engDrop = prev ? 0 : e.engagement_prev - e.engagement;
  return {
    eng: rwClamp(Math.max(engDrop / 20, (60 - eng) / 25)),
    abs: rwClamp((e.absence_days_90 - 2) / 8),
    perf: prev
      ? rwClamp((3 - perf) / 1.5)
      : rwClamp(Math.max((e.perf_prev - perf) / 1.5, (3 - perf) / 1.5)),
    pay: rwClamp((1 - e.compa_ratio) / 0.15),
    ot: rwClamp((e.overtime_hrs_wk - 42) / 12),
    stag: rwClamp((e.months_since_promo - 24) / 24),
    oo: rwClamp(e.oneonones_missed / 5),
    pto: rwClamp((e.pto_unused_days - 10) / 15),
    mgr: e.manager_changed ? 0.7 : 0,
  };
}
const rwScoreOf = (s) =>
  Math.round(RW_SIGNALS.reduce((a, g) => a + g.w * s[g.k], 0));
const rwTierOf = (v) =>
  v >= 60 ? "Critical" : v >= 40 ? "High" : v >= 25 ? "Watch" : "Stable";
const RW_TIERS = ["Critical", "High", "Watch", "Stable"];
const RW_TIER_COLOR = {
  Critical: THEME.resigned,
  High: "#D9661F",
  Watch: THEME.risk,
  Stable: THEME.retained,
};
function rwEnrich(e) {
  const s = rwSeverity(e, false);
  const sp = rwSeverity(e, true);
  const score = rwScoreOf(s);
  const prevScore = rwScoreOf(sp);
  const top = RW_SIGNALS.map((g) => ({ ...g, v: s[g.k] }))
    .filter((g) => g.v >= 0.4)
    .sort((a, b) => b.v * b.w - a.v * a.w);
  return {
    ...e,
    s,
    score,
    prevScore,
    delta: score - prevScore,
    tier: rwTierOf(score),
    prevTier: rwTierOf(prevScore),
    top,
  };
}

/* Fictional sample built on the dashboard's own functions and names */
const RW_SAMPLE = (() => {
  const r = mulberry32(42);
  const pick = (a) => a[Math.floor(r() * a.length)];
  const deptRisk = {
    "Manufacturing & Production": 0.35,
    "Sales & Marketing": 0.6,
    "Logistics & Supply Chain": 0.5,
    "Design & Packaging Development": 0.3,
    "Procurement & Raw Materials": 0.25,
    "Technology & Digital": 0.45,
  };
  const managers = {};
  DEPTS.forEach((dp, i) => {
    managers[dp.name] =
      `${FIRST[(i * 5 + 3) % FIRST.length]} ${LAST[(i * 7 + 2) % LAST.length]}`;
  });
  const out = [];
  for (let i = 0; i < 48; i++) {
    const d = DEPTS[i % DEPTS.length].name;
    const risk = rwClamp(deptRisk[d] + (r() - 0.5) * 0.9);
    const engPrev = Math.round(62 + r() * 30);
    const eng = Math.round(engPrev - risk * r() * 35 + (r() - 0.6) * 6);
    const perfPrev = +(3 + r() * 1.8).toFixed(1);
    const perf = +Math.max(
      1.5,
      Math.min(5, perfPrev - (risk > 0.6 ? r() * 1.4 : r() * 0.4 - 0.2)),
    ).toFixed(1);
    out.push({
      id: `ASP-${10421 + i * 7}`,
      name: `${FIRST[i % FIRST.length]} ${pick(LAST)}`,
      dept: d,
      role: pick(GENERIC_ROLES[d]),
      manager: managers[d],
      tenure_months: Math.round(4 + r() * 90),
      absence_days_90: Math.round(r() * 3 + risk * r() * 10),
      engagement: Math.max(20, eng),
      engagement_prev: engPrev,
      perf,
      perf_prev: perfPrev,
      overtime_hrs_wk: Math.round(38 + risk * r() * 20 + r() * 4),
      months_since_promo: Math.round(4 + r() * 28 + risk * r() * 24),
      compa_ratio: +(1.08 - risk * r() * 0.25 - r() * 0.05).toFixed(2),
      oneonones_missed: Math.round(risk * r() * 6),
      pto_unused_days: Math.round(r() * 12 + risk * r() * 12),
      manager_changed: r() < 0.12 + risk * 0.25 ? 1 : 0,
    });
  }
  return out;
})();

/* --- guided entry model --- */
const RW_META = {
  tenure_months: [0, 600],
  absence_days_90: [0, 90],
  engagement: [0, 100],
  engagement_prev: [0, 100],
  perf: [1, 5],
  perf_prev: [1, 5],
  overtime_hrs_wk: [0, 100],
  months_since_promo: [0, 600],
  compa_ratio: [0.3, 2],
  oneonones_missed: [0, 20],
  pto_unused_days: [0, 90],
};
const RW_STEPS = [
  {
    t: "Employee details",
    p: "Copy these from the employee’s profile in your HRMS.",
    src: ["hrms"],
    keys: ["id", "name", "dept", "role", "manager", "tenure_months"],
  },
  {
    t: "Attendance & leave",
    p: "From the leave register for the last 90 days. Count only leave taken without prior approval, such as sick days and same-day casual leave.",
    src: ["att"],
    keys: ["absence_days_90", "pto_unused_days"],
  },
  {
    t: "Engagement",
    p: "From your pulse survey. Use the 0–100 score; if your tool reports 1–5, multiply by 20.",
    src: ["survey"],
    keys: ["engagement", "engagement_prev"],
  },
  {
    t: "Performance",
    p: "The final, calibrated rating from the last two review cycles.",
    src: ["perf"],
    keys: ["perf", "perf_prev"],
  },
  {
    t: "Workload",
    p: "Hours from timesheets, 1:1s from the manager’s calendar, and reporting-line changes from the HRMS.",
    src: ["time", "mgr", "hrms"],
    keys: ["overtime_hrs_wk", "oneonones_missed", "manager_changed"],
  },
  {
    t: "Growth & pay",
    p: "Promotion date from job history; pay from payroll and your salary band sheet.",
    src: ["hrms", "pay"],
    keys: ["months_since_promo", "compa_ratio"],
  },
  {
    t: "Review & save",
    p: "Check the numbers, then save. You can change any section before saving.",
    src: [],
    keys: [],
  },
];
const RW_PERF_OPTS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
const RW_PERF_LBL = { 1: "Below", 3: "Meets", 5: "Outstanding" };
const RW_ERRKEY = {
  _ty: "tenure_months",
  _tm: "tenure_months",
  _py: "months_since_promo",
  _pm: "months_since_promo",
  _never: "months_since_promo",
  _paymode: "compa_ratio",
  _sal: "compa_ratio",
  _mid: "compa_ratio",
  _samePrev: "engagement_prev",
};
const rwNum = (v) => (v === "" || v == null ? NaN : Number(v));
const rwYm = (m) => (isNaN(m) ? "–" : `${Math.floor(m / 12)} yr ${m % 12} mo`);
const rwRatioSay = (r) =>
  isNaN(r)
    ? "Enter both amounts"
    : r < 0.9
      ? "Well below market"
      : r < 0.95
        ? "Below market"
        : r <= 1.05
          ? "At market"
          : "Above market";
function rwBlankDraft() {
  return {
    id: "",
    name: "",
    dept: "",
    role: "",
    manager: "",
    tenure_months: NaN,
    absence_days_90: 0,
    engagement: 70,
    engagement_prev: 70,
    perf: NaN,
    perf_prev: NaN,
    overtime_hrs_wk: 40,
    months_since_promo: NaN,
    compa_ratio: NaN,
    oneonones_missed: 0,
    pto_unused_days: 0,
    manager_changed: 0,
    _ty: "",
    _tm: "",
    _py: "",
    _pm: "",
    _never: false,
    _paymode: "band",
    _sal: "",
    _mid: "",
    _samePrev: false,
  };
}
function rwFromRecord(r) {
  return {
    ...rwBlankDraft(),
    ...r,
    _ty: Math.floor(r.tenure_months / 12),
    _tm: r.tenure_months % 12,
    _never: r.months_since_promo === r.tenure_months,
    _py: Math.floor(r.months_since_promo / 12),
    _pm: r.months_since_promo % 12,
    _paymode: "ratio",
  };
}
/* derives stored numbers from the helper inputs */
function rwSync(d0) {
  const d = { ...d0 };
  const ty = rwNum(d._ty);
  const tm = rwNum(d._tm);
  d.tenure_months = isNaN(ty) && isNaN(tm) ? NaN : (ty || 0) * 12 + (tm || 0);
  if (d._never) d.months_since_promo = d.tenure_months;
  else {
    const py = rwNum(d._py);
    const pm = rwNum(d._pm);
    d.months_since_promo =
      isNaN(py) && isNaN(pm) ? NaN : (py || 0) * 12 + (pm || 0);
  }
  if (d._samePrev) d.engagement_prev = d.engagement;
  if (d._paymode === "band") {
    const s = rwNum(d._sal);
    const m = rwNum(d._mid);
    d.compa_ratio = s > 0 && m > 0 ? Math.round((s / m) * 100) / 100 : NaN;
  }
  return d;
}
function rwStepErrors(i, d, records, editingId) {
  const errs = {};
  RW_STEPS[i].keys.forEach((k) => {
    if (RW_TEXT.includes(k)) {
      if (!String(d[k]).trim()) errs[k] = "Please fill this in.";
    } else if (k !== "manager_changed") {
      const v = d[k];
      const [a, b] = RW_META[k];
      if (typeof v !== "number" || isNaN(v))
        errs[k] =
          k === "compa_ratio"
            ? "Enter salary and band midpoint, or the ratio."
            : k.startsWith("perf")
              ? "Pick a rating."
              : "Please enter a number.";
      else if (v < a || v > b) errs[k] = `Must be between ${a} and ${b}.`;
    }
  });
  if (i === 0 && d.id && !errs.id) {
    if (!RW_ID_RE.test(d.id))
      errs.id = "Use letters, digits, dashes or dots, with no spaces.";
    else if (d.id !== editingId && records.some((r) => r.id === d.id))
      errs.id =
        "This ID already exists. Open it from “Saved employees” to edit.";
  }
  if (
    i === 5 &&
    !errs.months_since_promo &&
    d.months_since_promo > d.tenure_months
  )
    errs.months_since_promo = "Can’t be longer than their tenure.";
  return errs;
}
function rwToRecord(d) {
  const rec = {};
  RW_COLS.forEach((c) => {
    rec[c] = RW_TEXT.includes(c) ? String(d[c]).trim() : Number(d[c]);
  });
  rec.updated_at = new Date().toISOString();
  return rec;
}
function rwParseCsv(text) {
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .filter((l) => l.trim());
  if (!lines.length) throw new Error("The file is empty.");
  const split = (l) => {
    const o = [];
    let cur = "";
    let q = false;
    for (const ch of l) {
      if (ch === '"') q = !q;
      else if (ch === "," && !q) {
        o.push(cur);
        cur = "";
      } else cur += ch;
    }
    o.push(cur);
    return o.map((s) => s.trim());
  };
  const hdr = split(lines[0]).map((h) => h.toLowerCase());
  const miss = RW_COLS.filter((c) => !hdr.includes(c));
  if (miss.length)
    throw new Error(
      `Missing columns: ${miss.join(", ")}. Copy the header row above and match it exactly.`,
    );
  return lines.slice(1).map((l, i) => {
    const v = split(l);
    const o = {};
    RW_COLS.forEach((c) => {
      const x = v[hdr.indexOf(c)];
      o[c] = RW_TEXT.includes(c) ? x || "" : Number(x);
    });
    if (RW_COLS.slice(5).some((c) => isNaN(o[c])))
      throw new Error(
        `Row ${i + 2} has a non-numeric value in a numeric column.`,
      );
    if (!RW_ID_RE.test(o.id))
      throw new Error(
        `Row ${i + 2}: employee ID “${o.id}” can only use letters, digits, - _ . and no spaces.`,
      );
    if (!o.name) throw new Error(`Row ${i + 2} has no name.`);
    o.updated_at = new Date().toISOString();
    return o;
  });
}
const rwFmtVals = (e) => ({
  eng: `${e.engagement} (was ${e.engagement_prev})`,
  abs: `${e.absence_days_90} days`,
  perf: `${e.perf} (was ${e.perf_prev})`,
  pay: `${e.compa_ratio} compa-ratio`,
  ot: `${e.overtime_hrs_wk} hrs/wk`,
  stag: `${e.months_since_promo} months`,
  oo: `${e.oneonones_missed} missed`,
  pto: `${e.pto_unused_days} days unused`,
  mgr: e.manager_changed ? "Yes" : "No",
});
const rwShow = (v) =>
  v === "" || v == null || (typeof v === "number" && isNaN(v)) ? "–" : v;

/* --- small presentational pieces --- */
function RwTier({ tier, label }) {
  return (
    <span className={`rw-tier t-${tier.toLowerCase()}`}>{label || tier}</span>
  );
}
function RwTrack({ pct, color, w }) {
  return (
    <span className="rw-track" style={w ? { width: w } : undefined}>
      <i style={{ width: `${pct}%`, background: color }} />
    </span>
  );
}
function RwBreakdown({ e }) {
  const fmt = rwFmtVals(e);
  return (
    <div className="rw-brk">
      {RW_SIGNALS.map((g) => {
        const v = e.s[g.k];
        const c =
          v >= 0.7 ? THEME.resigned : v >= 0.4 ? RW_TIER_COLOR.High : "#b6bacb";
        return (
          <div className="rw-brk-row" key={g.k}>
            <div>
              {g.label}
              <small>{fmt[g.k]}</small>
            </div>
            <RwTrack pct={v * 100} color={c} />
            <span className="ap-num">
              {(g.w * v).toFixed(0)}/{g.w}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* --- Dashboard sub-tab --- */
const RW_PAGE = 12;
function RwDashboard({ data, masked, onOpen }) {
  const [deptFilter, setDeptFilter] = useState("");
  const [q, setQ] = useState("");
  const [tierF, setTierF] = useState("Watch and above");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const disp = (e, f) =>
    masked ? (f === "name" ? e.id : f === "manager" ? "Masked" : e[f]) : e[f];

  const n = data.length;
  const cnt = (t) => data.filter((e) => e.tier === t).length;
  const pcnt = (t) => data.filter((e) => e.prevTier === t).length;
  const avg = data.reduce((a, e) => a + e.score, 0) / n;
  const pavg = data.reduce((a, e) => a + e.prevScore, 0) / n;
  const flagged = RW_SIGNALS.map((g) => ({
    ...g,
    pct: Math.round((100 * data.filter((e) => e.s[g.k] >= 0.4).length) / n),
  })).sort((a, b) => b.pct - a.pct);
  const depts = [...new Set(data.map((e) => e.dept))].sort();
  const activeDept = depts.includes(deptFilter) ? deptFilter : "";
  const mx = Math.max(...RW_TIERS.map((t) => Math.max(cnt(t), pcnt(t))), 1);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const val = (e, k) => (k === "name" || k === "manager" ? disp(e, k) : e[k]);
    return data
      .filter(
        (e) =>
          (!activeDept || e.dept === activeDept) &&
          (tierF === "All tiers" ||
            (tierF === "Watch and above"
              ? e.tier !== "Stable"
              : e.tier === tierF)) &&
          (!s ||
            [disp(e, "name"), e.role, disp(e, "manager"), e.id]
              .join(" ")
              .toLowerCase()
              .includes(s)),
      )
      .sort((a, b) => {
        const x = val(a, sortKey);
        const y = val(b, sortKey);
        return (x > y ? 1 : x < y ? -1 : 0) * (sortDir === "asc" ? 1 : -1);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeDept, tierF, q, sortKey, sortDir, masked]);
  useEffect(() => setPage(1), [activeDept, tierF, q, sortKey, sortDir, masked]);
  const pages = Math.max(1, Math.ceil(rows.length / RW_PAGE));
  const start = (Math.min(page, pages) - 1) * RW_PAGE;
  const slice = rows.slice(start, start + RW_PAGE);
  const onSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir(k === "score" || k === "delta" ? "desc" : "asc");
    }
  };
  const heatBg = (v) =>
    `color-mix(in oklab, ${THEME.resigned} ${Math.round(6 + v * 0.85)}%, #ffffff)`;

  return (
    <>
      <section
        className="ap-kpi-grid rw-kpis"
        aria-label="Retention Watch indicators"
      >
        <Kpi
          label="Employees monitored"
          value={n}
          delta={null}
          deltaProps={{
            noPriorLabel: `${depts.length} function${depts.length === 1 ? "" : "s"}`,
          }}
          caption="in this snapshot"
        />
        <Kpi
          accent
          label="Critical risk"
          tip="Risk score 60 or above."
          value={cnt("Critical")}
          delta={cnt("Critical") - pcnt("Critical")}
          deltaProps={{ goodWhen: "down", digits: 0 }}
          caption={`${Math.round((100 * cnt("Critical")) / n)}% of workforce`}
        />
        <Kpi
          label="High risk"
          tip="Risk score 40 to 59."
          value={cnt("High")}
          delta={cnt("High") - pcnt("High")}
          deltaProps={{ goodWhen: "down", digits: 0 }}
          caption={`${Math.round((100 * cnt("High")) / n)}% of workforce`}
        />
        <Kpi
          label="Avg risk score"
          tip="Average 0–100 score across everyone monitored."
          value={avg}
          decimals={1}
          delta={avg - pavg}
          deltaProps={{ goodWhen: "down" }}
          caption="vs last quarter"
        />
        <Kpi
          label="Leading signal"
          value={flagged[0].pct}
          unit="%"
          delta={null}
          deltaProps={{
            noPriorLabel: flagged[0].pct ? flagged[0].label : "None",
          }}
          caption="of employees flagged"
        />
      </section>

      <section className="ap-card rw-block">
        <div className="ap-card-head">
          <div>
            <h2>Signal heatmap by function</h2>
            <p>
              Average signal severity (0–100) · select a function to filter the
              watchlist
            </p>
          </div>
          <div className="rw-legend" aria-hidden="true">
            Low
            {[0, 30, 60, 90].map((v) => (
              <i key={v} style={{ background: heatBg(v) }} />
            ))}
            Severe
          </div>
        </div>
        <div className="rw-scroll-x">
          <table className="rw-heat">
            <thead>
              <tr>
                <th />
                {RW_SIGNALS.map((g) => (
                  <th key={g.k}>{g.label}</th>
                ))}
                <th>Avg score</th>
                <th>At risk</th>
              </tr>
            </thead>
            <tbody>
              {depts.map((dp) => {
                const es = data.filter((e) => e.dept === dp);
                return (
                  <tr key={dp}>
                    <th className="rw-heat-dept">
                      <button
                        type="button"
                        className={activeDept === dp ? "is-on" : ""}
                        onClick={() =>
                          setDeptFilter(activeDept === dp ? "" : dp)
                        }
                      >
                        {dp}
                      </button>
                    </th>
                    {RW_SIGNALS.map((g) => {
                      const v =
                        (100 * es.reduce((a, e) => a + e.s[g.k], 0)) /
                        es.length;
                      return (
                        <td
                          key={g.k}
                          style={{
                            background: heatBg(v),
                            color: v > 55 ? "#fff" : THEME.ink,
                          }}
                        >
                          {Math.round(v)}
                        </td>
                      );
                    })}
                    <td className="rw-heat-meta">
                      {(
                        es.reduce((a, e) => a + e.score, 0) / es.length
                      ).toFixed(0)}
                    </td>
                    <td className="rw-heat-meta">
                      {es.filter((e) => e.score >= 40).length}/{es.length}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="rw-grid2">
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Risk tiers</h2>
              <p>This quarter vs last quarter</p>
            </div>
          </div>
          <div className="rw-tierbars">
            {RW_TIERS.map((t) => (
              <div className="rw-tb" key={t}>
                <RwTier tier={t} />
                <div className="rw-tb-bars">
                  <i
                    style={{
                      width: `${(100 * cnt(t)) / mx}%`,
                      background: RW_TIER_COLOR[t],
                    }}
                  />
                  <i
                    className="is-prev"
                    style={{
                      width: `${(100 * pcnt(t)) / mx}%`,
                      background: RW_TIER_COLOR[t],
                    }}
                  />
                </div>
                <span className="ap-num">
                  {cnt(t)} <span className="ap-muted">/ {pcnt(t)}</span>
                </span>
              </div>
            ))}
            <div className="rw-legend">
              <i style={{ background: THEME.muted }} /> This quarter
              <i
                style={{ background: THEME.muted, opacity: 0.35, height: 5 }}
              />{" "}
              Last quarter
            </div>
          </div>
        </section>
        <section className="ap-card">
          <div className="ap-card-head">
            <div>
              <h2>Most frequent signals</h2>
              <p>% of employees flagged</p>
            </div>
          </div>
          <div className="rw-sig-list">
            {flagged.slice(0, 6).map((g) => (
              <div className="rw-sig-row" key={g.k}>
                <span>{g.label}</span>
                <RwTrack pct={g.pct} color={THEME.ink} />
                <span className="ap-num">{g.pct}%</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="ap-card ap-card-flush">
        <div className="ap-card-head rw-pad">
          <div>
            <h2>Watchlist</h2>
            <p>
              {rows.length} of {n} employees
              {masked ? " · names masked" : " · select a row to open the case"}
            </p>
          </div>
        </div>
        <div className="ap-toolbar">
          <label className="ap-search">
            <Search size={15} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, role or manager"
              aria-label="Search watchlist"
            />
          </label>
          <Select
            label="Function"
            value={activeDept || ALL_DEPTS}
            onChange={(v) => setDeptFilter(v === ALL_DEPTS ? "" : v)}
            options={[ALL_DEPTS, ...depts]}
          />
          <Select
            label="Risk tier"
            value={tierF}
            onChange={setTierF}
            options={["Watch and above", "All tiers", ...RW_TIERS]}
          />
        </div>
        {rows.length === 0 ? (
          <EmptyState
            title="No employees match these filters"
            text="Try a different search or widen the risk tier."
            action="Clear filters"
            onAction={() => {
              setQ("");
              setDeptFilter("");
              setTierF("All tiers");
            }}
          />
        ) : (
          <>
            <div className="ap-table-wrap">
              <table className="ap-table rw-table">
                <thead>
                  <tr>
                    <Th
                      label="Employee"
                      k="name"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                      className="ap-sticky"
                    />
                    <Th
                      label="Function"
                      k="dept"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                      className="hide-md"
                    />
                    <Th
                      label="Manager"
                      k="manager"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                      className="hide-sm"
                    />
                    <Th
                      label="Risk score"
                      k="score"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <Th
                      label="Δ qtr"
                      k="delta"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                    <Th label="Tier" k="tier" />
                    <Th label="Top signals" k="top" className="hide-md" />
                  </tr>
                </thead>
                <tbody>
                  {slice.map((e) => (
                    <tr
                      key={e.id}
                      className={masked ? "is-locked" : ""}
                      tabIndex={masked ? -1 : 0}
                      onClick={() => !masked && onOpen(e)}
                      onKeyDown={(ev) =>
                        ev.key === "Enter" && !masked && onOpen(e)
                      }
                    >
                      <td className="ap-sticky">
                        <div className="ap-person">
                          {masked ? (
                            <span className="rw-mask-ico">
                              <EyeOff size={14} />
                            </span>
                          ) : (
                            <Avatar name={e.name} />
                          )}
                          <div>
                            <div className="ap-cell-title">
                              {disp(e, "name")}
                            </div>
                            <small>
                              {e.role} · {(e.tenure_months / 12).toFixed(1)} yrs
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="hide-md">{e.dept}</td>
                      <td className="hide-sm">{disp(e, "manager")}</td>
                      <td>
                        <div className="ap-bar-cell">
                          <RwTrack
                            pct={e.score}
                            color={RW_TIER_COLOR[e.tier]}
                            w={72}
                          />
                          <b>{e.score}</b>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`ap-num rw-delta ${e.delta > 0 ? "is-up" : e.delta < 0 ? "is-down" : ""}`}
                        >
                          {e.delta > 0 ? "+" : ""}
                          {e.delta}
                        </span>
                      </td>
                      <td>
                        <RwTier tier={e.tier} />
                      </td>
                      <td className="hide-md">
                        <div className="rw-chips">
                          {e.top.length ? (
                            e.top.slice(0, 3).map((g) => (
                              <span className="rw-chip" key={g.k}>
                                {g.label}
                              </span>
                            ))
                          ) : (
                            <span className="ap-muted">None</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={Math.min(page, pages)}
              pages={pages}
              onPage={setPage}
              from={start + 1}
              to={start + slice.length}
              total={rows.length}
            />
          </>
        )}
      </section>

      <details className="ap-card rw-method">
        <summary>How the score works</summary>
        <p>
          Each signal is converted to a severity from 0 to 1, multiplied by its
          weight, and summed to a 0–100 risk score. Tiers: Critical 60+, High
          40–59, Watch 25–39, Stable below 25. Last quarter’s score reuses the
          previous engagement and performance values. Scores are prompts for a
          conversation, not judgements about a person. Never use them alone for
          performance, pay or termination decisions.
        </p>
        <div className="ap-table-wrap">
          <table className="ap-table rw-plain">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Weight</th>
                <th>What triggers it</th>
                <th>Suggested response</th>
              </tr>
            </thead>
            <tbody>
              {RW_SIGNALS.map((g) => (
                <tr key={g.k}>
                  <td>{g.label}</td>
                  <td className="ap-num">{g.w}</td>
                  <td>{g.why}</td>
                  <td>{g.act}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

function RwCase({ e, canEdit, onEdit, onClose }) {
  const acts = e.top.slice(0, 4);
  return (
    <>
      <div className="ap-drawer-head">
        <div className="ap-drawer-id">
          <Avatar name={e.name} size={52} />
          <div>
            <h2>{e.name}</h2>
            <p>
              {e.role} · {e.dept}
            </p>
            <p className="ap-drawer-meta">
              <Briefcase size={12} />
              {e.id} · {(e.tenure_months / 12).toFixed(1)} years · reports to{" "}
              {e.manager}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="ap-icon-btn"
          onClick={onClose}
          aria-label="Close case"
        >
          <X size={18} />
        </button>
      </div>
      <div className="ap-drawer-body">
        <section className="ap-drawer-sec">
          <div className="rw-big">
            <span className="rw-big-num">{e.score}</span>
            <div>
              <RwTier tier={e.tier} />
              <div className="ap-muted rw-small">
                Last quarter {e.prevScore} ({e.prevTier})
              </div>
            </div>
          </div>
        </section>
        <section className="ap-drawer-sec">
          <h3>Signal breakdown</h3>
          <RwBreakdown e={e} />
        </section>
        <section className="ap-drawer-sec">
          <h3>Suggested next steps</h3>
          {acts.length ? (
            <ol className="rw-actions">
              {acts.map((g) => (
                <li key={g.k}>
                  <b>{g.label}:</b> {g.act}
                </li>
              ))}
            </ol>
          ) : (
            <p className="ap-muted">
              No signals above threshold. Continue regular check-ins.
            </p>
          )}
        </section>
        {canEdit && (
          <button
            type="button"
            className="ap-btn ap-btn-ghost"
            onClick={onEdit}
          >
            Edit record
          </button>
        )}
      </div>
    </>
  );
}

/* --- Enter data sub-tab --- */
function RwStepper({ k, unit, step, min, max, draft, setField }) {
  const v = draft[k];
  const bump = (dlt) => {
    const cur = rwNum(v);
    let nv = (isNaN(cur) ? 0 : cur) + dlt;
    nv = Math.max(min, Math.min(max, Math.round(nv * 100) / 100));
    setField(k, nv);
  };
  return (
    <div className="rw-stepper">
      <button type="button" onClick={() => bump(-step)} aria-label="Decrease">
        −
      </button>
      <input
        type="number"
        inputMode="decimal"
        id={`rw_i_${k}`}
        value={v === "" || Number.isNaN(v) ? "" : v}
        min={min}
        max={max}
        step={step}
        onChange={(e) =>
          setField(
            k,
            k.startsWith("_") ? e.target.value : rwNum(e.target.value),
          )
        }
      />
      <span className="rw-unit">{unit}</span>
      <button type="button" onClick={() => bump(step)} aria-label="Increase">
        +
      </button>
    </div>
  );
}
function RwQ({ k, label, hint, errs, htmlFor, children }) {
  return (
    <div className={`rw-q ${errs && errs[k] ? "is-bad" : ""}`}>
      {htmlFor ? (
        <label htmlFor={htmlFor}>{label}</label>
      ) : (
        <div className="rw-ql">{label}</div>
      )}
      {children}
      {hint && <div className="rw-hint">{hint}</div>}
      {errs && errs[k] && <div className="rw-err">{errs[k]}</div>}
    </div>
  );
}
function RwOpts({ k, items, draft, onPick }) {
  return (
    <div className="rw-opts">
      {items.map(([v, label, sub]) => (
        <button
          key={String(v)}
          type="button"
          className="rw-opt"
          aria-pressed={String(draft[k]) === String(v)}
          onClick={() => onPick(k, v)}
        >
          {label}
          {sub && <small>{sub}</small>}
        </button>
      ))}
    </div>
  );
}

function RwEntry({ form, records, raw, storeOk, persist, onSource }) {
  const {
    draft,
    setDraft,
    step,
    setStep,
    visited,
    setVisited,
    shownErrs,
    setShownErrs,
    editingId,
    setEditingId,
    msg,
    setMsg,
  } = form;
  const [busy, setBusy] = useState(false);
  const [rq, setRq] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const formRef = useRef(null);
  const S = RW_STEPS[step];
  const errs = shownErrs[step] || {};
  const pool = records.length ? records : raw;
  const deptList = [...new Set(pool.map((r) => r.dept))].sort();
  const mgrList = [...new Set(pool.map((r) => r.manager))].sort();
  const roleList = [...new Set(pool.map((r) => r.role))].sort();
  const stepOk = (i) =>
    !Object.keys(rwStepErrors(i, draft, records, editingId)).length;

  const clearErr = (k) => {
    const key = RW_ERRKEY[k] || k;
    if (!shownErrs[step] || !shownErrs[step][key]) return;
    const nextErrs = { ...shownErrs[step] };
    delete nextErrs[key];
    const all = { ...shownErrs };
    if (Object.keys(nextErrs).length) all[step] = nextErrs;
    else delete all[step];
    setShownErrs(all);
  };
  const setField = (k, v) => {
    clearErr(k);
    setDraft((d) => rwSync({ ...d, [k]: v }));
  };
  const pick = (k, v) => {
    clearErr(k);
    setDraft((d) => {
      const nd = {
        ...d,
        [k]: RW_TEXT.includes(k) || k === "_paymode" ? v : Number(v),
      };
      if (k === "perf" && isNaN(nd.perf_prev)) nd.perf_prev = nd.perf;
      return rwSync(nd);
    });
  };
  const go = (i) => {
    const s = Math.max(0, Math.min(RW_STEPS.length - 1, i));
    setStep(s);
    setVisited((v) => new Set(v).add(s));
    const el = formRef.current;
    if (el) {
      const t = el.getBoundingClientRect().top;
      if (t < 0 || t > window.innerHeight * 0.6)
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const resetForm = () => {
    setEditingId(null);
    setDraft(rwBlankDraft());
    setStep(0);
    setVisited(new Set([0]));
    setShownErrs({});
  };
  const next = () => {
    const e = rwStepErrors(step, draft, records, editingId);
    if (Object.keys(e).length) {
      setShownErrs({ ...shownErrs, [step]: e });
      setMsg({ err: true, text: "Fix the highlighted answers to continue." });
      return;
    }
    const all = { ...shownErrs };
    delete all[step];
    setShownErrs(all);
    setMsg(null);
    go(step + 1);
  };
  const doSave = async (another) => {
    for (let i = 0; i < RW_STEPS.length - 1; i++) {
      const e = rwStepErrors(i, draft, records, editingId);
      if (Object.keys(e).length) {
        setShownErrs({ ...shownErrs, [i]: e });
        go(i);
        setMsg({
          err: true,
          text: `“${RW_STEPS[i].t}” needs attention before saving.`,
        });
        return;
      }
    }
    const rec = rwToRecord(draft);
    const wasEdit = editingId;
    const sc = rwEnrich(rec);
    setBusy(true);
    setMsg({ text: "Saving…" });
    try {
      const list = records.filter((r) => r.id !== rec.id && r.id !== wasEdit);
      list.push(rec);
      await persist(list);
      const keep = { dept: draft.dept, manager: draft.manager };
      resetForm();
      if (another && !wasEdit) setDraft({ ...rwBlankDraft(), ...keep });
      setMsg({
        text: `${wasEdit ? "Updated" : "Saved"} ${rec.name} · score ${sc.score} (${sc.tier}).`,
      });
    } catch {
      setMsg({ err: true, text: "Could not save right now. Try again." });
    } finally {
      setBusy(false);
    }
  };
  const onSubmit = (ev) => {
    ev.preventDefault();
    if (step < RW_STEPS.length - 1) next();
    else doSave(true);
  };
  const startNewQuarter = () => {
    setDraft((d) => ({
      ...d,
      engagement_prev: d.engagement,
      perf_prev: d.perf,
      _samePrev: false,
    }));
    setMsg({
      text: "Last quarter’s values copied. Now enter this quarter’s numbers.",
    });
    go(1);
  };
  const startEdit = (id) => {
    const r = records.find((x) => x.id === id);
    if (!r) return;
    setEditingId(id);
    setDraft(rwSync(rwFromRecord(r)));
    setVisited(new Set(RW_STEPS.map((_, i) => i)));
    setShownErrs({});
    setMsg(null);
    setStep(0);
    if (formRef.current)
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onDelete = async (id) => {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      setTimeout(() => setPendingDelete((p) => (p === id ? null : p)), 4000);
      return;
    }
    setPendingDelete(null);
    try {
      await persist(records.filter((r) => r.id !== id));
      if (editingId === id) resetForm();
    } catch {
      setImportMsg({
        err: true,
        text: "Could not delete right now. Try again.",
      });
    }
  };
  const onFile = (ev) => {
    const f = ev.target.files[0];
    if (!f) return;
    const fr = new FileReader();
    fr.onload = async () => {
      try {
        const rows = rwParseCsv(String(fr.result));
        if (!rows.length)
          throw new Error("The file has a header but no employee rows.");
        const map = new Map(records.map((r) => [r.id, r]));
        rows.forEach((r) => map.set(r.id, r));
        await persist([...map.values()]);
        setImportMsg({ text: `Imported ${rows.length} employees.` });
      } catch (err) {
        setImportMsg({ err: true, text: err.message || "Import failed." });
      }
      ev.target.value = "";
    };
    fr.readAsText(f);
  };
  const copyHeader = () => {
    const t = RW_COLS.join(",");
    if (navigator.clipboard)
      navigator.clipboard.writeText(t).then(
        () => setImportMsg({ text: "Header row copied." }),
        () =>
          setImportMsg({
            text: "Select the header row above and press Ctrl+C.",
          }),
      );
  };

  /* live preview */
  const complete = Object.keys(RW_META).every(
    (k) => typeof draft[k] === "number" && !isNaN(draft[k]),
  );
  const pv = complete
    ? rwEnrich(draft)
    : rwEnrich({
        ...draft,
        tenure_months: draft.tenure_months || 0,
        perf: isNaN(draft.perf) ? 3 : draft.perf,
        perf_prev: isNaN(draft.perf_prev)
          ? isNaN(draft.perf)
            ? 3
            : draft.perf
          : draft.perf_prev,
        months_since_promo: isNaN(draft.months_since_promo)
          ? 0
          : draft.months_since_promo,
        compa_ratio: isNaN(draft.compa_ratio) ? 1 : draft.compa_ratio,
      });

  const stepper = (k, unit, st, min, max) => (
    <RwStepper
      k={k}
      unit={unit}
      step={st}
      min={min}
      max={max}
      draft={draft}
      setField={setField}
    />
  );
  const quick = (k, vals, unit = "") => (
    <RwOpts
      k={k}
      draft={draft}
      onPick={pick}
      items={vals.map((v) => [v, `${v}${unit}`])}
    />
  );
  const text = (k, label, ph, hint, list) => (
    <RwQ k={k} label={label} hint={hint} errs={errs} htmlFor={`rw_i_${k}`}>
      <input
        className="rw-tin"
        id={`rw_i_${k}`}
        value={draft[k]}
        placeholder={ph}
        autoComplete="off"
        list={list}
        onChange={(e) => setField(k, e.target.value)}
      />
    </RwQ>
  );
  const slider = (k, label, hint) => {
    const v = isNaN(draft[k]) ? 70 : draft[k];
    return (
      <RwQ k={k} label={label} hint={hint} errs={errs} htmlFor={`rw_i_${k}`}>
        <div className="rw-slider">
          <input
            type="range"
            id={`rw_i_${k}`}
            min={0}
            max={100}
            step={1}
            value={v}
            onChange={(e) => setField(k, Number(e.target.value))}
          />
          <output>{v}</output>
        </div>
        <div className="rw-scale">
          <span>0 · Disengaged</span>
          <span>50</span>
          <span>100 · Highly engaged</span>
        </div>
      </RwQ>
    );
  };

  const body = [
    () => (
      <>
        <div className="rw-qgrid">
          {text(
            "id",
            "Employee ID",
            "ASP-10421",
            "As it appears in the HRMS. Letters, digits and dashes.",
          )}
          {text("name", "Full name", "Kavya Menon")}
        </div>
        <RwQ k="dept" label="Function" errs={errs} htmlFor="rw_i_dept">
          <input
            className="rw-tin"
            id="rw_i_dept"
            value={draft.dept}
            placeholder="Type a function or tap one below"
            onChange={(e) => setField("dept", e.target.value)}
          />
          <RwOpts
            k="dept"
            draft={draft}
            onPick={pick}
            items={deptList.map((d) => [d, d])}
          />
        </RwQ>
        <div className="rw-qgrid">
          {text(
            "role",
            "Role / designation",
            "Press Operator",
            "",
            "rw-dl-role",
          )}
          {text(
            "manager",
            "Reporting manager",
            "Priya Nair",
            "Start typing to pick a manager already entered.",
            "rw-dl-mgr",
          )}
        </div>
        <RwQ
          k="tenure_months"
          label="How long have they worked here?"
          hint="From the date of joining."
          errs={errs}
        >
          <div className="rw-inline">
            {stepper("_ty", "years", 1, 0, 50)}
            {stepper("_tm", "months", 1, 0, 11)}
          </div>
        </RwQ>
        <datalist id="rw-dl-mgr">
          {mgrList.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <datalist id="rw-dl-role">
          {roleList.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      </>
    ),
    () => (
      <div className="rw-qgrid">
        <RwQ
          k="absence_days_90"
          label="Unplanned leave days in the last 90 days"
          hint="Half days count as 0.5. Planned and approved leave does not count."
          errs={errs}
        >
          {stepper("absence_days_90", "days", 0.5, 0, 90)}
          {quick("absence_days_90", [0, 2, 4, 6, 8, 10])}
        </RwQ>
        <RwQ
          k="pto_unused_days"
          label="Unused annual leave balance"
          hint="A large balance late in the year can mean someone is not taking breaks."
          errs={errs}
        >
          {stepper("pto_unused_days", "days", 0.5, 0, 90)}
          {quick("pto_unused_days", [0, 5, 10, 15, 20])}
        </RwQ>
      </div>
    ),
    () => (
      <>
        {slider(
          "engagement",
          "Engagement score now",
          "Latest pulse survey result.",
        )}
        {!draft._samePrev &&
          slider(
            "engagement_prev",
            "Engagement score last quarter",
            "Previous pulse survey result.",
          )}
        <label className="rw-tick">
          <input
            type="checkbox"
            checked={draft._samePrev}
            onChange={(e) => setField("_samePrev", e.target.checked)}
          />
          No previous survey for this person (use the same score)
        </label>
      </>
    ),
    () => (
      <>
        <RwQ
          k="perf"
          label="Current rating"
          hint="1 = below expectations, 3 = meets, 5 = outstanding."
          errs={errs}
        >
          <RwOpts
            k="perf"
            draft={draft}
            onPick={pick}
            items={RW_PERF_OPTS.map((v) => [v, v, RW_PERF_LBL[v]])}
          />
        </RwQ>
        <RwQ
          k="perf_prev"
          label="Previous cycle rating"
          hint="If this is their first cycle, pick the same as the current rating."
          errs={errs}
        >
          <RwOpts
            k="perf_prev"
            draft={draft}
            onPick={pick}
            items={RW_PERF_OPTS.map((v) => [v, v, RW_PERF_LBL[v]])}
          />
        </RwQ>
      </>
    ),
    () => (
      <>
        <div className="rw-qgrid">
          <RwQ
            k="overtime_hrs_wk"
            label="Average hours worked per week"
            hint="Last 8 weeks. A standard week is about 40–45 hours."
            errs={errs}
          >
            {stepper("overtime_hrs_wk", "hrs/week", 1, 0, 100)}
            {quick("overtime_hrs_wk", [40, 45, 50, 55, 60])}
          </RwQ>
          <RwQ
            k="oneonones_missed"
            label="1:1 meetings missed"
            hint="Recurring 1:1s cancelled or skipped by either side."
            errs={errs}
          >
            {stepper("oneonones_missed", "in 8 weeks", 1, 0, 20)}
            {quick("oneonones_missed", [0, 1, 2, 3, 4])}
          </RwQ>
        </div>
        <RwQ
          k="manager_changed"
          label="Has their manager changed in the last 6 months?"
          errs={errs}
        >
          <RwOpts
            k="manager_changed"
            draft={draft}
            onPick={pick}
            items={[
              [0, "No"],
              [1, "Yes"],
            ]}
          />
        </RwQ>
      </>
    ),
    () => (
      <>
        <RwQ
          k="months_since_promo"
          label="Time since last promotion"
          hint="Count from the last grade or title change."
          errs={errs}
        >
          <div className="rw-inline">
            {!draft._never && stepper("_py", "years", 1, 0, 50)}
            {!draft._never && stepper("_pm", "months", 1, 0, 11)}
            <label className="rw-tick">
              <input
                type="checkbox"
                checked={draft._never}
                onChange={(e) => setField("_never", e.target.checked)}
              />
              Never promoted here (uses tenure)
            </label>
          </div>
        </RwQ>
        <RwQ k="compa_ratio" label="Pay compared with the market" errs={errs}>
          <RwOpts
            k="_paymode"
            draft={draft}
            onPick={pick}
            items={[
              ["band", "Enter salary and band"],
              ["ratio", "I know the compa-ratio"],
            ]}
          />
          {draft._paymode === "band" ? (
            <>
              <div className="rw-qgrid">
                <div className="rw-q">
                  <label htmlFor="rw_i__sal" className="rw-small-label">
                    Monthly gross salary (₹)
                  </label>
                  <input
                    className="rw-tin"
                    type="number"
                    inputMode="numeric"
                    id="rw_i__sal"
                    value={draft._sal}
                    placeholder="85000"
                    onChange={(e) => setField("_sal", e.target.value)}
                  />
                </div>
                <div className="rw-q">
                  <label htmlFor="rw_i__mid" className="rw-small-label">
                    Pay band midpoint for the role (₹)
                  </label>
                  <input
                    className="rw-tin"
                    type="number"
                    inputMode="numeric"
                    id="rw_i__mid"
                    value={draft._mid}
                    placeholder="95000"
                    onChange={(e) => setField("_mid", e.target.value)}
                  />
                </div>
              </div>
              <div className="rw-calc">
                <span>Compa-ratio</span>
                <b className="ap-num">
                  {isNaN(draft.compa_ratio)
                    ? "–"
                    : draft.compa_ratio.toFixed(2)}
                </b>
                <span className="rw-hint">{rwRatioSay(draft.compa_ratio)}</span>
              </div>
              <div className="rw-hint">
                Only the ratio is saved. Salary figures are not stored.
              </div>
            </>
          ) : (
            <>
              {stepper("compa_ratio", "ratio", 0.01, 0.3, 2)}
              <div className="rw-hint">
                Salary ÷ band midpoint. 1.00 means paid at the market midpoint.
              </div>
            </>
          )}
        </RwQ>
      </>
    ),
    () => {
      const e = rwEnrich(draft);
      const sections = [
        [
          0,
          "Employee",
          [
            ["ID", draft.id],
            ["Name", draft.name],
            ["Function", draft.dept],
            ["Role", draft.role],
            ["Manager", draft.manager],
            ["Tenure", rwYm(draft.tenure_months)],
          ],
        ],
        [
          1,
          "Attendance & leave",
          [
            ["Unplanned leave", `${draft.absence_days_90} days`],
            ["Unused annual leave", `${draft.pto_unused_days} days`],
          ],
        ],
        [
          2,
          "Engagement",
          [
            ["Now", draft.engagement],
            ["Last quarter", draft.engagement_prev],
          ],
        ],
        [
          3,
          "Performance",
          [
            ["Current", draft.perf],
            ["Previous", draft.perf_prev],
          ],
        ],
        [
          4,
          "Workload",
          [
            ["Hours per week", draft.overtime_hrs_wk],
            ["1:1s missed", draft.oneonones_missed],
            ["Manager changed", draft.manager_changed ? "Yes" : "No"],
          ],
        ],
        [
          5,
          "Growth & pay",
          [
            ["Since promotion", rwYm(draft.months_since_promo)],
            [
              "Compa-ratio",
              isNaN(draft.compa_ratio) ? "–" : draft.compa_ratio.toFixed(2),
            ],
          ],
        ],
      ];
      return (
        <>
          <div className="rw-review">
            {sections.map(([s, t, kv]) => (
              <div className="rw-rv" key={s}>
                <div className="rw-rh">
                  <b>{t}</b>
                  <button
                    type="button"
                    className="ap-link"
                    onClick={() => go(s)}
                  >
                    Change
                  </button>
                </div>
                <dl>
                  {kv.map(([a, b]) => (
                    <React.Fragment key={a}>
                      <dt>{a}</dt>
                      <dd>{rwShow(b)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          {e.top.length > 0 && (
            <div className="ap-callout">
              <Lightbulb size={16} />
              <div>
                <b>What stands out</b>
                <ul className="rw-actions">
                  {e.top.slice(0, 3).map((g) => (
                    <li key={g.k}>
                      <b>{g.label}:</b> {g.act}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      );
    },
  ];

  const recRows = records
    .filter(
      (r) =>
        !rq.trim() ||
        `${r.name} ${r.id}`.toLowerCase().includes(rq.trim().toLowerCase()),
    )
    .map(rwEnrich)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {!storeOk && (
        <div className="rw-banner is-muted">
          <Info size={15} />
          <span>
            Saving is unavailable in this view, so entries stay in this browser
            tab only and disappear when you reload.
          </span>
        </div>
      )}
      {editingId && (
        <div className="rw-banner">
          <span>
            Editing <b>{draft.name}</b> ({editingId}).
          </span>
          <button
            type="button"
            className="ap-btn ap-btn-ghost ap-btn-sm"
            onClick={startNewQuarter}
            title="Moves this quarter’s engagement and rating into ‘last quarter’"
          >
            Start new quarter update
          </button>
          <button
            type="button"
            className="ap-btn ap-btn-ghost ap-btn-sm"
            onClick={() => {
              resetForm();
              setMsg(null);
            }}
          >
            Cancel editing
          </button>
        </div>
      )}
      <ol className="rw-rail" aria-label="Form steps">
        {RW_STEPS.map((s, i) => {
          const done =
            visited.has(i) && i !== step && stepOk(i) && !shownErrs[i];
          return (
            <li key={s.t}>
              <button
                type="button"
                aria-current={i === step ? "step" : undefined}
                className={shownErrs[i] ? "is-err" : done ? "is-done" : ""}
                onClick={() => {
                  setMsg(null);
                  go(i);
                }}
              >
                <span className="rw-dot">
                  {done ? <Check size={13} /> : i + 1}
                </span>
                {s.t}
              </button>
            </li>
          );
        })}
      </ol>
      <div className="rw-entry">
        <form
          className="ap-card rw-stepcard"
          ref={formRef}
          noValidate
          onSubmit={onSubmit}
        >
          <div className="rw-stephead">
            <div>
              <div className="ap-eyebrow">
                Step {step + 1} of {RW_STEPS.length}
              </div>
              <h2>{S.t}</h2>
              <p>{S.p}</p>
            </div>
            <div className="rw-srcs">
              {S.src.map((k) => (
                <button
                  key={k}
                  type="button"
                  className="rw-src-badge"
                  onClick={() => onSource(k)}
                  title="See where this comes from"
                >
                  <i style={{ background: RW_SRC[k].hue }} />
                  From {RW_SRC[k].name}
                </button>
              ))}
            </div>
          </div>
          <div className="rw-stepbody">{body[step]()}</div>
          <div className="rw-navbar">
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              style={{ visibility: step ? "visible" : "hidden" }}
              onClick={() => {
                setMsg(null);
                go(step - 1);
              }}
            >
              <ChevronLeft size={15} /> Back
            </button>
            <span
              className={`rw-msg ${msg && msg.err ? "is-err" : ""}`}
              role="status"
            >
              {msg ? msg.text : ""}
            </span>
            <div className="rw-navr">
              {step < RW_STEPS.length - 1 ? (
                <button type="submit" className="ap-btn ap-btn-primary">
                  Next <ChevronRight size={15} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="ap-btn ap-btn-ghost"
                    disabled={busy}
                    onClick={() => doSave(false)}
                  >
                    {editingId ? "Save changes" : "Save"}
                  </button>
                  <button
                    type="submit"
                    className="ap-btn ap-btn-primary"
                    disabled={busy}
                  >
                    {editingId ? "Save & finish" : "Save & add another"}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
        <aside className="ap-card rw-preview" aria-live="polite">
          <div className="ap-eyebrow">Live score preview</div>
          <div className="rw-big">
            <span className="rw-big-num">{pv.score}</span>
            <div>
              {complete ? (
                <RwTier tier={pv.tier} />
              ) : (
                <RwTier tier="Watch" label="So far" />
              )}
              <div className="ap-muted rw-small">
                {complete
                  ? `Last quarter ${pv.prevScore} (${pv.prevTier})`
                  : "Score updates as you answer. Unanswered items count as neutral."}
              </div>
            </div>
          </div>
          <RwBreakdown e={pv} />
        </aside>
      </div>

      <section className="ap-card ap-card-flush">
        <div className="ap-card-head rw-pad">
          <div>
            <h2>Saved employees</h2>
            <p>{records.length} saved</p>
          </div>
        </div>
        <div className="ap-toolbar">
          <label className="ap-search">
            <Search size={15} />
            <input
              value={rq}
              onChange={(e) => setRq(e.target.value)}
              placeholder="Find a record by name or ID"
              aria-label="Find record"
            />
          </label>
        </div>
        {!records.length ? (
          <EmptyState
            title="No employees saved yet"
            text="Use the form above or import a CSV. The dashboard shows sample data until then."
          />
        ) : !recRows.length ? (
          <EmptyState
            title={`No records match “${rq}”`}
            text="Try a different name or ID."
          />
        ) : (
          <div className="ap-table-wrap">
            <table className="ap-table rw-plain">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="hide-sm">Function</th>
                  <th>Score</th>
                  <th>Tier</th>
                  <th className="hide-sm">Last updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recRows.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <div className="ap-cell-title">{e.name}</div>
                      <small className="ap-muted">
                        {e.id} · {e.role}
                      </small>
                    </td>
                    <td className="hide-sm">{e.dept}</td>
                    <td className="ap-num">{e.score}</td>
                    <td>
                      <RwTier tier={e.tier} />
                    </td>
                    <td className="hide-sm ap-muted">
                      {e.updated_at
                        ? new Date(e.updated_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "–"}
                    </td>
                    <td>
                      <div className="rw-rowact">
                        <button
                          type="button"
                          className="ap-btn ap-btn-ghost ap-btn-sm"
                          onClick={() => startEdit(e.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ap-btn ap-btn-ghost ap-btn-sm rw-danger"
                          onClick={() => onDelete(e.id)}
                        >
                          {pendingDelete === e.id ? "Confirm delete" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ap-card rw-import">
        <div className="ap-card-head">
          <div>
            <h2>Bulk import from CSV</h2>
            <p>
              Have an HRMS export? Upload a CSV with one row per employee and
              this header row. Rows with an existing employee ID update that
              record.
            </p>
          </div>
        </div>
        <code>{RW_COLS.join(",")}</code>
        <div className="rw-row">
          <button
            type="button"
            className="ap-btn ap-btn-ghost"
            onClick={copyHeader}
          >
            Copy header row
          </button>
          <label className="ap-btn ap-btn-primary" htmlFor="rw-file">
            <Upload size={15} /> Choose CSV file
          </label>
          <input
            type="file"
            id="rw-file"
            accept=".csv,text/csv"
            hidden
            onChange={onFile}
          />
          <span
            className={`rw-msg ${importMsg && importMsg.err ? "is-err" : ""}`}
            role="status"
          >
            {importMsg ? importMsg.text : ""}
          </span>
        </div>
      </section>
    </>
  );
}

/* --- Data sources sub-tab --- */
function RwFlow({ sel, onSelect }) {
  const IX = 300,
    IY = 150,
    IW = 180,
    IH = 260;
  const box = (x, y, w, h, t, sub, cls = "rw-n-box") => (
    <g>
      <rect className={cls} x={x} y={y} width={w} height={h} rx="10" />
      <text
        className="rw-n-title"
        x={x + w / 2}
        y={y + h / 2 - 4}
        textAnchor="middle"
      >
        {t}
      </text>
      <text
        className="rw-n-sub"
        x={x + w / 2}
        y={y + h / 2 + 14}
        textAnchor="middle"
      >
        {sub}
      </text>
    </g>
  );
  const core = (id, d, lbl, lx, ly, anchor = "middle") => (
    <g>
      <path id={id} className="rw-wire is-core" d={d} markerEnd="url(#rw-ah)" />
      <path className="rw-flowline" d={d} />
      <circle className="rw-pulse" r="4">
        <animateMotion dur="1.8s" repeatCount="indefinite">
          <mpath href={`#${id}`} />
        </animateMotion>
      </circle>
      {lbl && (
        <text className="rw-edge-l" x={lx} y={ly} textAnchor={anchor}>
          {lbl}
        </text>
      )}
    </g>
  );
  return (
    <svg
      viewBox="0 0 1040 560"
      role="img"
      aria-label="Seven source systems feed HR's monthly data intake, which saves employee records, scores nine warning signals, and serves a named HR view and a masked stakeholder view."
    >
      <defs>
        <marker
          id="rw-ah"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill={THEME.ink} />
        </marker>
      </defs>
      {[
        ["1 · Source systems", 10],
        ["2 · Monthly intake", 300],
        ["3 · Store & score", 540],
        ["4 · Who sees it", 790],
      ].map(([t, x]) => (
        <text key={t} className="rw-col-h" x={x} y="18">
          {t}
        </text>
      ))}
      {RW_SOURCES.map((src, i) => {
        const cy = 34 + i * 74 + 29;
        const ty = IY + 24 + i * ((IH - 48) / 6);
        const d = `M232,${cy} C268,${cy} 264,${ty} ${IX},${ty}`;
        const on = sel === src.k;
        return (
          <g key={`w_${src.k}`}>
            <path
              id={`rw_w_${src.k}`}
              className={`rw-wire ${on ? "is-on" : ""}`}
              d={d}
            />
            <path className="rw-flowline" d={d} />
            <circle className={`rw-pulse ${on ? "" : "is-dim"}`} r="3.5">
              <animateMotion
                dur={`${2.4 + i * 0.23}s`}
                repeatCount="indefinite"
                begin={`-${(i * 0.37).toFixed(2)}s`}
              >
                <mpath href={`#rw_w_${src.k}`} />
              </animateMotion>
            </circle>
          </g>
        );
      })}
      {RW_SOURCES.map((src, i) => {
        const y = 34 + i * 74;
        return (
          <g
            key={src.k}
            className={`rw-n-src ${sel === src.k ? "is-on" : ""}`}
            tabIndex={0}
            role="button"
            aria-label={`${src.name}: ${src.sub}`}
            onClick={() => onSelect(src.k)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(src.k);
              }
            }}
          >
            <rect
              className="rw-n-box"
              x="10"
              y={y}
              width="222"
              height="58"
              rx="9"
            />
            <rect x="10" y={y} width="5" height="58" rx="2" fill={src.hue} />
            <text className="rw-n-title" x="26" y={y + 28}>
              {src.name}
            </text>
            <text className="rw-n-sub" x="26" y={y + 46}>
              {src.sub}
            </text>
            <text className="rw-n-tag" x="222" y={y + 16} textAnchor="end">
              {src.freq.toUpperCase()}
            </text>
          </g>
        );
      })}
      <rect
        className="rw-n-core"
        x={IX}
        y={IY}
        width={IW}
        height={IH}
        rx="12"
      />
      <text
        className="rw-n-title"
        x={IX + IW / 2}
        y={IY + 28}
        textAnchor="middle"
      >
        HR data intake
      </text>
      <text
        className="rw-n-sub"
        x={IX + IW / 2}
        y={IY + 45}
        textAnchor="middle"
      >
        once a month
      </text>
      <rect
        className="rw-n-inner"
        x={IX + 16}
        y={IY + 66}
        width={IW - 32}
        height="70"
        rx="8"
      />
      <text
        className="rw-n-title"
        x={IX + IW / 2}
        y={IY + 95}
        textAnchor="middle"
        style={{ fontSize: 12.5 }}
      >
        Guided entry
      </text>
      <text
        className="rw-n-sub"
        x={IX + IW / 2}
        y={IY + 113}
        textAnchor="middle"
      >
        one employee at a time
      </text>
      <text
        className="rw-edge-l"
        x={IX + IW / 2}
        y={IY + 156}
        textAnchor="middle"
      >
        or
      </text>
      <rect
        className="rw-n-inner"
        x={IX + 16}
        y={IY + 170}
        width={IW - 32}
        height="70"
        rx="8"
      />
      <text
        className="rw-n-title"
        x={IX + IW / 2}
        y={IY + 199}
        textAnchor="middle"
        style={{ fontSize: 12.5 }}
      >
        CSV import
      </text>
      <text
        className="rw-n-sub"
        x={IX + IW / 2}
        y={IY + 217}
        textAnchor="middle"
      >
        whole team in one file
      </text>
      {core("rw_c1", `M${IX + IW},245 L545,245`, "saves", 512, 237)}
      {box(550, 210, 180, 70, "Employee records", "one row per person")}
      {core("rw_c2", "M640,280 L640,326", "reads", 650, 307, "start")}
      {box(
        550,
        330,
        180,
        90,
        "Scoring engine",
        "9 signals × weights",
        "rw-n-core",
      )}
      <text className="rw-n-tag" x="640" y={330 + 76} textAnchor="middle">
        → 0–100 RISK SCORE
      </text>
      {box(790, 120, 240, 80, "HR view", "named watchlist + case detail")}
      {box(
        790,
        270,
        240,
        80,
        "Actions",
        "stay interview · comp review · workload",
      )}
      {box(790, 420, 240, 80, "Stakeholder view", "masked names · dept trends")}
      {core(
        "rw_c3",
        "M730,360 C760,360 755,160 786,160",
        "full detail",
        782,
        148,
        "end",
      )}
      {core("rw_c4", "M910,200 L910,266", "triggers", 920, 238, "start")}
      {core(
        "rw_c5",
        "M730,390 C760,390 755,460 786,460",
        "masked",
        782,
        480,
        "end",
      )}
    </svg>
  );
}

function RwSources({ sel, setSel, onFormStep }) {
  const [touring, setTouring] = useState(true);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!touring || reduce) return undefined;
    const t = setInterval(() => {
      setSel((cur) => {
        const i = RW_SOURCES.findIndex((s) => s.k === cur);
        return RW_SOURCES[(i + 1) % RW_SOURCES.length].k;
      });
    }, 3200);
    return () => clearInterval(t);
  }, [touring, setSel]);
  const choose = (k) => {
    setTouring(false);
    setSel(k);
  };
  const src = RW_SRC[sel];
  const fields = RW_FIELDMAP.filter((f) => f.src === sel);
  return (
    <>
      <figure className="rw-flowfig">
        <div className="ap-card rw-flowwrap">
          <RwFlow sel={sel} onSelect={choose} />
        </div>
        <figcaption>
          Each month HR pulls figures from seven systems, enters them through
          the guided form or a CSV import, and Retention Watch scores them.
          Select a source to see exactly which fields it supplies.
        </figcaption>
      </figure>
      <section className="ap-card">
        <div className="rw-srcdetail">
          <div>
            <div className="ap-eyebrow rw-src-eyebrow">
              <i style={{ background: src.hue }} />
              {src.freq} pull · owner: {src.owner}
            </div>
            <h2 className="rw-src-title">{src.name}</h2>
            <p className="ap-muted">
              {src.sub}. Typical systems: {src.eg}.
            </p>
          </div>
          <div>
            <div className="ap-eyebrow">Fields it supplies</div>
            <ul className="rw-fields">
              {fields.map((f) => (
                <li key={f.f}>
                  <b>{f.f}</b> <span className="ap-muted">· {f.where}</span>
                </li>
              ))}
            </ul>
            <div className="rw-row">
              {[...new Set(fields.map((f) => f.step))].map((i) => (
                <button
                  key={i}
                  type="button"
                  className="ap-btn ap-btn-ghost ap-btn-sm"
                  onClick={() => onFormStep(i)}
                >
                  Enter in step {i + 1}: {RW_STEPS[i].t}{" "}
                  <ArrowRight size={13} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="ap-card ap-card-flush">
        <div className="ap-card-head rw-pad">
          <div>
            <h2>Field-by-field source map</h2>
            <p>Hand this to whoever pulls the monthly numbers</p>
          </div>
        </div>
        <div className="ap-table-wrap">
          <table className="ap-table rw-plain">
            <thead>
              <tr>
                <th>Field</th>
                <th>Source system</th>
                <th>Where to find it</th>
                <th>Form step</th>
              </tr>
            </thead>
            <tbody>
              {RW_FIELDMAP.map((f) => (
                <tr
                  key={f.f}
                  className={f.src === sel ? "rw-row-on" : ""}
                  onClick={() => choose(f.src)}
                >
                  <td>
                    <b>{f.f}</b>
                  </td>
                  <td>
                    <span className="rw-src-name">
                      <i style={{ background: RW_SRC[f.src].hue }} />
                      {RW_SRC[f.src].name}
                    </span>
                  </td>
                  <td>{f.where}</td>
                  <td>
                    {f.step + 1}. {RW_STEPS[f.step].t}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* --- page --- */
function RetentionWatchView() {
  const [tab, setTab] = useState("dash");
  const [records, setRecords] = useState([]);
  const storeOk = typeof window !== "undefined" && !!window.storage;
  const [audience, setAudienceState] = useState(() => {
    try {
      return localStorage.getItem("rw-view") === "sh" ? "sh" : "hr";
    } catch {
      return "hr";
    }
  });
  const [caseEmp, setCaseEmp] = useState(null);
  const lastCase = useRef(null);
  if (caseEmp) lastCase.current = caseEmp;
  const [sel, setSel] = useState("hrms");

  /* form state lives here so a draft survives switching sub-tabs */
  const [draft, setDraft] = useState(rwBlankDraft);
  const [step, setStep] = useState(0);
  const [visited, setVisited] = useState(() => new Set([0]));
  const [shownErrs, setShownErrs] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState(null);
  const form = {
    draft,
    setDraft,
    step,
    setStep,
    visited,
    setVisited,
    shownErrs,
    setShownErrs,
    editingId,
    setEditingId,
    msg,
    setMsg,
  };

  useEffect(() => {
    if (!storeOk) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await window.storage.get(RW_STORE_KEY, true);
        if (!cancelled && res && res.value) {
          const list = JSON.parse(res.value);
          if (Array.isArray(list)) setRecords(list.filter((r) => r && r.id));
        }
      } catch {
        /* nothing stored yet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeOk]);

  const persist = useCallback(
    async (list) => {
      setRecords(list);
      if (storeOk)
        await window.storage.set(RW_STORE_KEY, JSON.stringify(list), true);
    },
    [storeOk],
  );
  const setAudience = (v) => {
    setAudienceState(v);
    try {
      localStorage.setItem("rw-view", v);
    } catch {
      /* preference not kept */
    }
  };

  const isSample = !records.length;
  const data = useMemo(
    () => (isSample ? RW_SAMPLE : records).map(rwEnrich),
    [isSample, records],
  );
  const masked = audience === "sh";
  const asOf = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const startEdit = (id) => {
    const r = records.find((x) => x.id === id);
    if (!r) return;
    setCaseEmp(null);
    setEditingId(id);
    setDraft(rwSync(rwFromRecord(r)));
    setVisited(new Set(RW_STEPS.map((_, i) => i)));
    setShownErrs({});
    setMsg(null);
    setStep(0);
    setTab("enter");
  };
  const toFormStep = (i) => {
    setStep(i);
    setVisited((v) => new Set(v).add(i));
    setTab("enter");
    window.scrollTo({ top: 0 });
  };
  const toSource = (k) => {
    setSel(k);
    setTab("sources");
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="ap-view rw">
      <PageHeader
        eyebrow={`People risk · As of ${asOf}`}
        title="Retention Watch"
        subtitle="Early warning signals for flight risk and burnout, scored from attendance, engagement, performance, workload, growth and pay data."
      >
        <div className="ap-seg" role="group" aria-label="Audience view">
          {[
            ["hr", "HR view", Eye],
            ["sh", "Stakeholder view", EyeOff],
          ].map(([k, l, Icon]) => (
            <button
              key={k}
              type="button"
              aria-pressed={audience === k}
              className={audience === k ? "is-on" : ""}
              onClick={() => setAudience(k)}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
      </PageHeader>

      <nav
        className="rw-tabs"
        role="tablist"
        aria-label="Retention Watch sections"
      >
        {[
          ["dash", "Dashboard"],
          ["enter", "Enter data"],
          ["sources", "Data sources"],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={tab === k ? "is-on" : ""}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </nav>

      {isSample && tab !== "sources" && (
        <div className="rw-banner">
          <Info size={15} />
          <span>
            <b>Sample data.</b> Names and figures are fictional examples. Add
            employees in{" "}
            <button
              type="button"
              className="ap-link"
              onClick={() => setTab("enter")}
            >
              Enter data
            </button>{" "}
            and the dashboard switches to your records.
          </span>
        </div>
      )}
      {masked && tab === "dash" && (
        <div className="rw-banner is-muted">
          <EyeOff size={15} />
          <span>
            <b>Stakeholder view.</b> Names and managers are masked and
            individual case detail is hidden. Use this view when presenting to
            business leaders.
          </span>
        </div>
      )}

      {tab === "dash" && (
        <RwDashboard data={data} masked={masked} onOpen={setCaseEmp} />
      )}
      {tab === "enter" && (
        <RwEntry
          form={form}
          records={records}
          raw={RW_SAMPLE}
          storeOk={storeOk}
          persist={persist}
          onSource={toSource}
        />
      )}
      {tab === "sources" && (
        <RwSources sel={sel} setSel={setSel} onFormStep={toFormStep} />
      )}

      <PrivacyNote>
        Retention Watch scores are prompts for a supportive conversation, not
        judgements. Restricted to authorized HR users.
      </PrivacyNote>

      <Drawer
        open={!!caseEmp && !masked}
        onClose={() => setCaseEmp(null)}
        label="Retention case"
      >
        {lastCase.current && (
          <RwCase
            e={lastCase.current}
            canEdit={
              !isSample && records.some((r) => r.id === lastCase.current.id)
            }
            onEdit={() => startEdit(lastCase.current.id)}
            onClose={() => setCaseEmp(null)}
          />
        )}
      </Drawer>
    </div>
  );
}

/* ---------------------------------------------------------------
   APP SHELL
--------------------------------------------------------------- */
const NAV = [
  {
    group: "Insights",
    items: [
      {
        id: "dashboard",
        label: "Retention Analysis",
        short: "Retention",
        icon: Gauge,
      },
      { id: "employees", label: "Employees", short: "Employees", icon: Users },
      {
        id: "watch",
        label: "Retention Watch",
        short: "Watch",
        icon: Activity,
      },
      {
        id: "talent",
        label: "Critical Talent Pool",
        short: "Talent",
        icon: ShieldCheck,
      },
      {
        id: "departments",
        label: "Departments",
        short: "Functions",
        icon: Building2,
      },
    ],
  },
  {
    group: "Employee listening",
    items: [
      { id: "survey", label: "Take Survey", icon: MessageSquare },
      { id: "cycles", label: "Survey Cycles", icon: CalendarRange },
    ],
  },
  {
    group: "Administration",
    items: [
      { id: "settings", label: "Model Settings", icon: SlidersHorizontal },
    ],
  },
];
const MOBILE_PRIMARY = ["dashboard", "employees", "talent", "departments"];

export default function RetentionDashboard({ skipLogin = false }) {
  const [authed, setAuthed] = useState(skipLogin);
  const [view, setView] = useState("dashboard");
  const [quarter, setQuarter] = useState(QUARTERS[QUARTERS.length - 1]);
  const [unit, setUnit] = useState(ALL_UNITS);
  const [dept, setDept] = useState(ALL_DEPTS);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [live, setLive] = useState({});
  const [person, setPerson] = useState(null);
  const [deptDetail, setDeptDetail] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const lastPerson = useRef(null);
  if (person) lastPerson.current = person;

  /* persisted live survey responses (shared storage if available) */
  useEffect(() => {
    const store = typeof window !== "undefined" ? window.storage : null;
    if (!store) return undefined;
    let cancelled = false;
    (async () => {
      const out = {};
      for (let i = 0; i < QUARTERS.length; i++) {
        try {
          const res = await store.get(`responses:${i}`, true);
          if (res && res.value) out[i] = JSON.parse(res.value);
        } catch {
          /* nothing stored */
        }
      }
      if (!cancelled) setLive(out);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const qIndex = QUARTERS.indexOf(quarter);
  const getSnap = useCallback(
    (i) => SNAPSHOTS[i].concat((live[i] || []).map(enrich)),
    [live],
  );
  const unitOnly = useCallback(
    (list) => (unit === ALL_UNITS ? list : list.filter((e) => e.unit === unit)),
    [unit],
  );
  const applyF = useCallback(
    (list) => {
      let l = unitOnly(list);
      if (dept !== ALL_DEPTS) l = l.filter((e) => e.department === dept);
      return l;
    },
    [unitOnly, dept],
  );

  const filtered = useMemo(
    () => applyF(getSnap(qIndex)),
    [applyF, getSnap, qIndex],
  );
  const withRisk = useMemo(
    () =>
      filtered.map((e) => {
        const risk = computeRisk(e.params, weights);
        return { ...e, risk, bucket: riskBucket(risk) };
      }),
    [filtered, weights],
  );
  const series = useMemo(
    () => QUARTERS.map((q, i) => ({ q, ...stats(applyF(getSnap(i))) })),
    [applyF, getSnap],
  );
  const cur = series[qIndex];
  const prev = qIndex > 0 ? series[qIndex - 1] : null;

  const riskSegments = useMemo(() => {
    const n = (b) => withRisk.filter((e) => e.bucket === b).length;
    return ["Low", "Medium", "High"].map((b) => ({
      key: b,
      label: RISK_META[b].label,
      hint: RISK_META[b].hint,
      color: RISK_META[b].color,
      value: n(b),
    }));
  }, [withRisk]);

  const driverRows = useMemo(() => {
    const g = { Retained: [], "At risk": [], Resigned: [] };
    filtered.forEach((e) => g[e.status].push(e));
    const a = driverAvg(g.Retained),
      b = driverAvg(g["At risk"]),
      c = driverAvg(g.Resigned);
    return PARAM_KEYS.map((k) => ({
      key: k,
      parameter: PARAM_LABELS[k],
      Retained: a[k],
      "At risk": b[k],
      Resigned: c[k],
    }));
  }, [filtered]);

  const deptRows = useMemo(() => {
    const snaps = QUARTERS.map((_, i) => unitOnly(getSnap(i)));
    return DEPTS.map((dp) => {
      const per = snaps.map((sn) =>
        stats(sn.filter((e) => e.department === dp.name)),
      );
      const s = per[qIndex];
      const sp = qIndex > 0 ? per[qIndex - 1] : null;
      const dr = driverAvg(
        snaps[qIndex].filter((e) => e.department === dp.name),
      );
      const ranked = PARAM_KEYS.slice().sort((a, b) => dr[b] - dr[a]);
      return {
        name: dp.name,
        s,
        sp,
        delta: sp && sp.total ? s.rate - sp.rate : null,
        atRiskPct: s.total ? (s.atRisk / s.total) * 100 : 0,
        rateSeries: per.map((p) => p.rate),
        resignedSeries: per.map((p) => p.resigned),
        strongest: { key: ranked[0], val: dr[ranked[0]] },
        weakest: {
          key: ranked[ranked.length - 1],
          val: dr[ranked[ranked.length - 1]],
        },
      };
    });
  }, [unitOnly, getSnap, qIndex]);

  const talent = useMemo(
    () => buildTalent(filtered, weights),
    [filtered, weights],
  );

  const go = useCallback((id) => {
    setView(id);
    setMoreOpen(false);
    setNotifOpen(false);
    if (id !== "departments") setDeptDetail(null);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }, []);

  /* insights derived from real data */
  const insights = useMemo(() => {
    const nowU = unitOnly(getSnap(qIndex));
    const prevU = qIndex > 0 ? unitOnly(getSnap(qIndex - 1)) : null;
    const rows = DEPTS.map((dp) => {
      const n = nowU.filter((e) => e.department === dp.name);
      const p = prevU ? prevU.filter((e) => e.department === dp.name) : [];
      const share = (l) =>
        l.length
          ? (l.filter((e) => e.status !== "Retained").length / l.length) * 100
          : 0;
      const dn = driverAvg(n);
      const dpv = p.length ? driverAvg(p) : null;
      let worst = null;
      PARAM_KEYS.forEach((k) => {
        const val = dpv ? dn[k] - dpv[k] : dn[k];
        if (!worst || val < worst.val) worst = { key: k, val };
      });
      return {
        name: dp.name,
        n: n.length,
        share: share(n),
        shareDelta: p.length ? share(n) - share(p) : null,
        worst,
        hasPrev: !!dpv,
        protect: buildTalent(n, weights).active.filter(
          (e) => e.quadrant === "protect",
        ).length,
      };
    }).filter((r) => r.n > 0);
    const used = new Set();
    const take = (sorted) => {
      const r = sorted.find((x) => !used.has(x.name)) || sorted[0];
      if (r) used.add(r.name);
      return r;
    };
    const out = [];
    const a = take(
      rows
        .slice()
        .sort((x, y) => (y.shareDelta ?? y.share) - (x.shareDelta ?? x.share)),
    );
    if (a)
      out.push({
        tone: "warning",
        tag: "Retention risk",
        title: `${a.name} - retention risk is ${a.shareDelta != null && a.shareDelta > 0 ? "rising" : "elevated"}`,
        detail:
          a.shareDelta != null
            ? `${a.share.toFixed(1)}% of employees are At risk or Resigned, ${a.shareDelta >= 0 ? "up" : "down"} ${Math.abs(a.shareDelta).toFixed(1)} pts vs the previous cycle.`
            : `${a.share.toFixed(1)}% of employees are At risk or Resigned this cycle.`,
        cta: "View function",
        onClick: () => {
          setDeptDetail(a.name);
          go("departments");
        },
      });
    const b = take(rows.slice().sort((x, y) => x.worst.val - y.worst.val));
    if (b)
      out.push({
        tone: "critical",
        tag: "Driver decline",
        title: `${b.name} - ${PARAM_LABELS[b.worst.key].toLowerCase()} ${b.hasPrev ? "score declining" : "is the weakest driver"}`,
        detail: b.hasPrev
          ? `Average score moved ${b.worst.val.toFixed(1)} points vs the previous cycle.`
          : `Average score is ${b.worst.val.toFixed(1)} out of 10.`,
        cta: "Explore drivers",
        onClick: () => {
          setDeptDetail(b.name);
          go("departments");
        },
      });
    const c = take(rows.slice().sort((x, y) => y.protect - x.protect));
    if (c)
      out.push({
        tone: "neutral",
        tag: "Talent exposure",
        title: `${c.name} - critical talent exposure ${c.protect > 0 ? "needs review" : "is low"}`,
        detail: `${c.protect} critical colleague${c.protect === 1 ? "" : "s"} combine higher retention risk with high criticality.`,
        cta: "Open talent pool",
        onClick: () => {
          setDept(c.name);
          go("talent");
        },
      });
    return out;
  }, [unitOnly, getSnap, qIndex, weights, go]);

  const submitSurvey = useCallback(
    async (response, targetQ) => {
      const key = `${response.name.trim().toLowerCase()}|${response.department}`;
      const existing = live[targetQ] || [];
      const deduped = existing.filter(
        (e) => `${e.name.trim().toLowerCase()}|${e.department}` !== key,
      );
      const updated = [...deduped, response];
      setLive({ ...live, [targetQ]: updated });
      try {
        if (typeof window !== "undefined" && window.storage)
          await window.storage.set(
            `responses:${targetQ}`,
            JSON.stringify(updated),
            true,
          );
      } catch {
        /* keep in-session update */
      }
    },
    [live],
  );

  const ctx = {
    quarter,
    setQuarter,
    qIndex,
    unit,
    setUnit,
    dept,
    setDept,
    weights,
    setWeights,
    series,
    cur,
    prev,
    filtered,
    withRisk,
    riskSegments,
    driverRows,
    deptRows,
    talent,
    insights,
    getSnap,
    unitOnly,
    deptDetail,
    setDeptDetail,
    go,
    submitSurvey,
    openPerson: (e) => setPerson(e),
  };

  if (!authed)
    return (
      <div className="ap-root">
        <Login onSignIn={() => setAuthed(true)} />
      </div>
    );

  const renderNav = (item, extra) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        type="button"
        className={`ap-nav-item ${view === item.id ? "is-active" : ""}`}
        onClick={() => go(item.id)}
        aria-current={view === item.id ? "page" : undefined}
        data-tip={item.label}
        {...extra}
      >
        <Icon size={18} strokeWidth={1.8} />
        <span>{item.label}</span>
      </button>
    );
  };
  const flat = NAV.flatMap((g) => g.items);

  return (
    <DataCtx.Provider value={ctx}>
      <div className="ap-root ap-shell">
        <aside className="ap-sidebar">
          <div className="ap-sidebar-brand">
            <Wordmark light />
          </div>
          <nav className="ap-nav" aria-label="Primary">
            {NAV.map((g) => (
              <div className="ap-nav-group" key={g.group}>
                <div className="ap-nav-group-label">{g.group}</div>
                {g.items.map((it) => renderNav(it))}
              </div>
            ))}
          </nav>
          <div className="ap-sidebar-foot">
            <div className="ap-org">
              <span className="ap-org-dot" />
              <div>
                <b>Ashtavinayak Packaging</b>
                <small>Corporate HR · Prototype Mode</small>
              </div>
            </div>
            <div className="ap-user">
              <Avatar name="John Doe" size={34} />
              <div>
                <b>John Doe</b>
                <small>People Analytics Lead</small>
              </div>
              <button
                type="button"
                className="ap-icon-btn is-dark"
                onClick={() => setAuthed(false)}
                aria-label="Sign out"
                data-tip="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </aside>

        <div className="ap-main">
          <div className="ap-topbar">
            <div className="ap-topbar-brand">
              <Wordmark compact />
            </div>
            <PrivacyNote />
            <div className="ap-topbar-actions">
              <div className="ap-pop-wrap">
                <button
                  type="button"
                  className="ap-icon-btn"
                  aria-label="Notifications"
                  onClick={() => setNotifOpen(!notifOpen)}
                >
                  <Bell size={17} />
                  <i className="ap-dot" />
                </button>
                {notifOpen && (
                  <div className="ap-pop" role="menu">
                    <b>Notifications</b>
                    <p>
                      <CircleCheck size={14} /> {quarter} survey cycle closed at
                      {SURVEY_PARTICIPATION[qIndex]}% participation.
                    </p>
                    <p>
                      <AlertTriangle size={14} /> {insights.length} areas
                      flagged for attention in the latest cycle.
                    </p>
                    <p>
                      <ShieldCheck size={14} /> Critical talent continuity
                      review is due this quarter.
                    </p>
                  </div>
                )}
              </div>
              <button type="button" className="ap-icon-btn" aria-label="Help">
                <HelpCircle size={17} />
              </button>
            </div>
          </div>
          <main
            className={`ap-content ${view === "survey" ? "is-survey" : ""}`}
          >
            {view === "dashboard" && <DashboardView />}
            {view === "employees" && <EmployeesView />}
            {view === "watch" && <RetentionWatchView />}
            {view === "talent" && <TalentView />}
            {view === "departments" && <DepartmentsView />}
            {view === "cycles" && <CyclesView />}
            {view === "survey" && <SurveyView />}
            {view === "settings" && <SettingsView />}
            <footer className="ap-footer">
              Ashtavinayak Packaging · People Intelligence · All employee data
              shown is fictional sample data.
            </footer>
          </main>
        </div>

        <nav className="ap-bottomnav" aria-label="Mobile primary">
          {flat
            .filter((i) => MOBILE_PRIMARY.includes(i.id))
            .map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.id}
                  type="button"
                  className={view === it.id ? "is-active" : ""}
                  onClick={() => go(it.id)}
                >
                  <Icon size={19} strokeWidth={1.8} />
                  <span>{it.short}</span>
                </button>
              );
            })}
          <button
            type="button"
            className={!MOBILE_PRIMARY.includes(view) ? "is-active" : ""}
            onClick={() => setMoreOpen(true)}
          >
            <MoreHorizontal size={19} />
            <span>More</span>
          </button>
        </nav>
        {moreOpen && (
          <div className="ap-sheet-root">
            <div
              className="ap-drawer-scrim"
              onClick={() => setMoreOpen(false)}
            />
            <div className="ap-sheet" role="dialog" aria-label="More">
              <div className="ap-sheet-handle" />
              {flat
                .filter((i) => !MOBILE_PRIMARY.includes(i.id))
                .map((it) => {
                  const Icon = it.icon;
                  return (
                    <button key={it.id} type="button" onClick={() => go(it.id)}>
                      <Icon size={18} />
                      {it.label}
                      <ChevronRight size={15} />
                    </button>
                  );
                })}
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  setAuthed(false);
                }}
              >
                <LogOut size={18} />
                Sign out
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}

        <Drawer
          open={!!person}
          onClose={() => setPerson(null)}
          label="Employee profile"
        >
          {lastPerson.current && (
            <EmployeeDrawer
              emp={lastPerson.current}
              onClose={() => setPerson(null)}
            />
          )}
        </Drawer>
      </div>
    </DataCtx.Provider>
  );
}
